// DPREdit.jsx - Complete with full backend integration
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import { Icon } from "../../icons";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import AppToast from "../../components/AppToast";
import PhotoUploadWithDrawing from "../../components/siteCheck/PhotoUploadWithDrawing";
import PhotoUploadWithDrawingDPR from "../../components/siteCheck/PhotoUploadWithDrawingDPR";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import useFormDraft from "../../hooks/useFormDraft";

/* ========================= MASTER DATA ========================= */

const MATERIAL_MASTER = [
  { name: "Cement (OPC/PPC)", unit: "Bags" },
  { name: "Steel/TMT Bars", unit: "MT" },
  { name: "Coarse Aggregate (20mm)", unit: "Cum" },
  { name: "Coarse Aggregate (10mm)", unit: "Cum" },
  { name: "Fine Aggregate / Sand", unit: "Cum" },
  { name: "Bricks", unit: "Nos" },
  { name: "Blocks", unit: "Nos" },
  { name: "Ready Mix Concrete", unit: "Cum" },
  { name: "Concrete Blocks", unit: "Nos" },
  { name: "Plywood / Shuttering", unit: "Sqm" },
  { name: "Binding Wire", unit: "Kg" },
  { name: "Nails", unit: "Kg" },
  { name: "MS Channels / Angles", unit: "MT" },
  { name: "Tile", unit: "Sqm" },
  { name: "Tile Adhesive", unit: "Kg" },
  { name: "Block Jointing Chemical", unit: "Ltr" },
];

const EQUIPMENT_MASTER = [
  "Excavator",
  "JCB",
  "Concrete Mixer",
  "Tower Crane",
  "Mobile Crane",
  "Concrete Pumping",
  "Vibrator",
  "Compactor",
  "Bar Bending Machine",
  "Bar Cutting Machine",
  "Welding Machine",
  "Generator / DG Set",
  "RMC Transit Mixer",
  "Concrete Pump (Static)",
  "Hydra Crane",
  "Wheel Loader",
  "Roller",
  "Total Station",
  "Auto Level",
  "Tractor",
  "Water Tanker",
  "Other"
];

/* ========================= MAIN ========================= */

export default function DPREdit() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token, userData } = useAuth();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [dprData, setDprData] = useState({
    workProgress: [],
    materialReceived: [],
    machinery: [],
    qualityControl: [],
    safetyObservations: [],
    visitorLogs: [],
    delaysHindrances: [],
    photographs: []
  });
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: ""
  });

  // Get date from location state or use today's date
  const reportDate = location.state?.date || new Date().toISOString().split('T')[0];
  const dprId = location.state?.dprId;

  // Show toast notification
  const showToast = (type, title, message) => {
    setToast({
      open: true,
      type,
      title,
      message
    });
  };

  // Fetch project and DPR data
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (loading) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }
        setError(null);

        const config = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        };

        // Fetch project data
        const projectResponse = await axios.get(
          `${url}/api/inhouse-projects/fetch-by/${projectId}`,
          config
        );

        if (projectResponse.data.project) {
          setProject(projectResponse.data.project);
        } else {
          throw new Error("Project not found");
        }

        // Fetch DPR data for this date
        await fetchDPRData(reportDate, config);

        setLoading(false);
        setRefreshing(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data. Please try again.");
        setLoading(false);
        setRefreshing(false);
      }
    };

    if (projectId && token) {
      fetchData();
    }
  }, [projectId, token, reportDate]);

  // Fetch DPR data for the given date
  // const fetchDPRData = async (date, config) => {
  //   try {
  //     const response = await axios.get(
  //       `${url}/api/dpr/fetch`,
  //       {
  //         ...config,
  //         params: {
  //           projectId,
  //           reportDate: date,
  //         }
  //       }
  //     );

  //     console.log("DPR API Response:", response.data);

  //     if (response.data.dprs) {
  //       console.log("DPR Groups:", response.data.dprs);
  //       // Group DPRs by report type
  //       const groupedData = {
  //         workProgress: [],
  //         materialReceived: [],
  //         machineryReport: [],
  //         qualityControl: [],
  //         safetyObservations: [],
  //         visitorLogs: [],
  //         delaysHindrances: [],
  //         photographs: []
  //       };

  //       response.data.dprs.forEach(group => {
  //         if (group.entries) {
  //           switch(group.reportType) {
  //             case "Work Progress & Manpower Deployment":
  //               groupedData.workProgress = group.entries.map(entry => ({
  //                 id: entry._id,
  //                 work: entry.workProgressDetails?.workDescription || "",
  //                 contractor: entry.workProgressDetails?.contractorName || "",
  //                 skilled: entry.workProgressDetails?.skilledLabour ? 
  //                   Object.entries(entry.workProgressDetails.skilledLabour).map(([type, qty]) => `${type} - ${qty}`) : [],
  //                 unskilled: entry.workProgressDetails?.unskilledLabour ? 
  //                   Object.entries(entry.workProgressDetails.unskilledLabour).map(([type, qty]) => `${type} - ${qty}`) : [],
  //                 qty: entry.workProgressDetails?.estimatedQuantity || 0,
  //                 unit: entry.workProgressDetails?.unit || "Person",
  //                 remark: entry.workProgressDetails?.remarks || entry.adminRemarks || "",
  //                 entryData: entry
  //               }));
  //               break;
  //             case "Material Received Report":
  //               groupedData.materialReceived = group.entries.map(entry => ({
  //                 id: entry._id,
  //                 material: entry.materialDescription || "",
  //                 supplier: entry.supplierName || "",
  //                 challan: entry.challanNumber || "",
  //                 unit: entry.unit || "",
  //                 opening: entry.materialDetails?.openingBalance || 0,
  //                 received: entry.materialDetails?.receivedToday || 0,
  //                 total: entry.materialDetails?.totalReceived || 0,
  //                 consumed: entry.materialDetails?.consumedToday || 0,
  //                 closing: entry.materialDetails?.closingBalance || 0,
  //                 remark: entry.adminRemarks || "",
  //                 entryData: entry
  //               }));
  //               break;
  //             case "Machinery Report":
  //               groupedData.machineryReport = group.entries.map(entry => ({
  //                 id: entry._id,
  //                 equipment: entry.machineryDetails?.equipmentName || "",
  //                 supplier: entry.machineryDetails?.supplierName || "",
  //                 inTime: entry.machineryDetails?.inTime || "",
  //                 outTime: entry.machineryDetails?.outTime || "",
  //                 working: entry.machineryDetails?.workingHours || "",
  //                 planned: entry.machineryDetails?.plannedQty || 0,
  //                 actual: entry.machineryDetails?.actualQty || 0,
  //                 remark: entry.machineryDetails?.remarks || entry.adminRemarks || "",
  //                 entryData: entry
  //               }));
  //               break;
  //             case "Quality Control & Testing":
  //               groupedData.qualityControl = group.entries.map(entry => ({
  //                 id: entry._id,
  //                 description: entry.qualityControlDetails?.testDescription || "",
  //                 location: entry.qualityControlDetails?.locationElement || "",
  //                 date: entry.qualityControlDetails?.testDate ? 
  //                   new Date(entry.qualityControlDetails.testDate).toLocaleDateString("en-GB", {
  //                     day: "2-digit",
  //                     month: "short",
  //                     year: "numeric"
  //                   }) : "",
  //                 count: entry.qualityControlDetails?.numberOfTests || 0,
  //                 result: entry.qualityControlDetails?.results || "",
  //                 status: entry.qualityControlDetails?.status || "",
  //                 remark: entry.qualityControlDetails?.remarks || entry.adminRemarks || "",
  //                 entryData: entry
  //               }));
  //               break;
  //             case "Safety Observation":
  //               groupedData.safetyObservations = group.entries.map(entry => ({
  //                 id: entry._id,
  //                 item: entry.safetyObservationDetails?.itemName || "",
  //                 status: entry.safetyObservationDetails?.status || "",
  //                 remark: entry.safetyObservationDetails?.remarks || entry.adminRemarks || "",
  //                 entryData: entry
  //               }));
  //               break;
  //             case "Visitor Log":
  //               groupedData.visitorLogs = group.entries.map(entry => ({
  //                 id: entry._id,
  //                 name: entry.visitorLogDetails?.visitorName || "",
  //                 company: entry.visitorLogDetails?.companyName || "",
  //                 designation: entry.visitorLogDetails?.designation || "",
  //                 purpose: entry.visitorLogDetails?.purposeOfVisit || "",
  //                 instructions: entry.visitorLogDetails?.instructionsDecisions || "",
  //                 timeIn: entry.visitorLogDetails?.timeIn || "",
  //                 timeOut: entry.visitorLogDetails?.timeOut || "",
  //                 remark: entry.visitorLogDetails?.remarks || entry.adminRemarks || "",
  //                 entryData: entry
  //               }));
  //               break;
  //             case "Delay Hindrances":
  //               groupedData.delaysHindrances = group.entries.map(entry => ({
  //                 id: entry._id,
  //                 description: entry.delayHindrancesDetails?.descriptionOfDelay || "",
  //                 duration: entry.delayHindrancesDetails?.duration || "",
  //                 reason: entry.delayHindrancesDetails?.reason || "",
  //                 remark: entry.delayHindrancesDetails?.remarks || entry.adminRemarks || "",
  //                 entryData: entry
  //               }));
  //               break;
  //             case "Photograph":
  //               groupedData.photographs = group.entries.map(entry => ({
  //                 id: entry._id,
  //                 src: entry.photographDetails?.photoPath || "https://via.placeholder.com/300x200?text=Photo",
  //                 activity: entry.photographDetails?.activityLocation || "",
  //                 ref: entry.photographDetails?.photoReferenceNo || "",
  //                 remark: entry.photographDetails?.remarks || entry.adminRemarks || "",
  //                 entryData: entry
  //               }));
  //               break;
  //           }
  //         }
  //       });

  //       // Add prefilled materials for missing entries
  //       const existingMaterials = groupedData.materialReceived.map(m => m.material);
  //       const missingMaterials = MATERIAL_MASTER.filter(master => 
  //         !existingMaterials.some(existing => existing === master.name)
  //       );

  //       missingMaterials.forEach(material => {
  //         groupedData.materialReceived.push({
  //           id: `prefill-${material.name}`,
  //           material: material.name,
  //           supplier: "",
  //           challan: "",
  //           unit: material.unit,
  //           opening: 0,
  //           received: 0,
  //           total: 0,
  //           consumed: 0,
  //           closing: 0,
  //           remark: ""
  //         });
  //       });

  //       // Add prefilled equipment for missing entries
  //       const existingEquipment = groupedData.machineryReport.map(m => m.equipment);
  //       const missingEquipment = EQUIPMENT_MASTER.filter(equipment => 
  //         !existingEquipment.some(existing => existing === equipment)
  //       );

  //       missingEquipment.forEach(equipment => {
  //         groupedData.machineryReport.push({
  //           id: `prefill-${equipment}`,
  //           equipment: equipment,
  //           supplier: "",
  //           inTime: "",
  //           outTime: "",
  //           working: "",
  //           planned: 0,
  //           actual: 0,
  //           remark: ""
  //         });
  //       });

  //       setDprData(groupedData);
  //     }
  //   } catch (err) {
  //     console.error("Error fetching DPR data:", err);
  //     showToast("error", "Error", "Failed to load DPR data");
  //   }
  // };
  // Fetch DPR data for the given date
  const fetchDPRData = async (date, config) => {
    try {
      const response = await axios.get(
        `${url}/api/dpr/fetch`,
        {
          ...config,
          params: {
            projectId,
            reportDate: date,
            limit: 100, // Add this like in DPR.jsx
            page: 1,    // Add this like in DPR.jsx
          }
        }
      );

      console.log("DPR API Response:", response.data);

      if (response.data.dprs && Array.isArray(response.data.dprs)) {
        console.log("DPR Groups:", response.data.dprs);

        // Initialize empty grouped data
        const groupedData = {
          workProgress: [],
          materialReceived: [],
          machinery: [],
          qualityControl: [],
          safetyObservations: [],
          visitorLogs: [],
          delaysHindrances: [],
          photographs: []
        };

        // Process each group like in DPR.jsx
        response.data.dprs.forEach((dprGroup, groupIndex) => {
          console.log(`\n=== Group ${groupIndex + 1} ===`);
          console.log("Report Type:", dprGroup.reportType);
          console.log("Entries Count:", dprGroup.entries?.length || 0);

          if (dprGroup.entries && Array.isArray(dprGroup.entries)) {
            dprGroup.entries.forEach((entry, entryIndex) => {
              console.log(`  Entry ${entryIndex + 1}:`, {
                id: entry._id,
                title: entry.title,
                materialDescription: entry.materialDescription,
                hasMaterialDetails: !!entry.materialDetails
              });

              // Process based on report type
              switch (dprGroup.reportType) {
                case "Work Progress & Manpower Deployment":
                  const skilledLabour = entry.workProgressDetails?.skilledLabour || {};
                  const unskilledLabour = entry.workProgressDetails?.unskilledLabour || {};

                  groupedData.workProgress.push({
                    id: entry._id,
                    work: entry.workProgressDetails?.workDescription || "",
                    contractor: entry.workProgressDetails?.contractorName || "",
                    skilled: Object.entries(skilledLabour).map(([type, qty]) => `${type} - ${qty}`),
                    unskilled: Object.entries(unskilledLabour).map(([type, qty]) => `${type} - ${qty}`),
                    qty: entry.workProgressDetails?.estimatedQuantity || 0,
                    unit: entry.workProgressDetails?.unit || "Person", // Use actual unit from database
                    remark: entry.adminRemarks || "-",
                    entryData: entry
                  });
                  break;

                case "Material Received Report":
                  console.log("  Material Details:", entry.materialDetails);
                  groupedData.materialReceived.push({
                    id: entry._id,
                    material: entry.materialDescription || "",
                    supplier: entry.supplierName || "",
                    challan: entry.challanNumber || "",
                    unit: entry.unit || "",
                    opening: entry.materialDetails?.openingBalance || 0,
                    received: entry.materialDetails?.receivedToday || 0,
                    total: entry.materialDetails?.totalReceived || 0,
                    consumed: entry.materialDetails?.consumedToday || 0,
                    closing: entry.materialDetails?.closingBalance || 0,
                    remark: entry.adminRemarks || "-",
                    entryData: entry
                  });
                  break;

                case "Machinery Report":
                  groupedData.machinery.push({
                    id: entry._id,
                    equipment: entry.machineryDetails?.equipmentName || "",
                    supplier: entry.machineryDetails?.supplierName || "",
                    startReading: entry.machineryDetails?.startReading || 0,
                    endReading: entry.machineryDetails?.endReading || 0,
                    inTime: entry.machineryDetails?.inTime || "",
                    outTime: entry.machineryDetails?.outTime || "",
                    working: entry.machineryDetails?.workingHours || "",
                    planned: entry.machineryDetails?.plannedQty || 0,
                    actual: entry.machineryDetails?.actualQty || 0,
                    remark: entry.adminRemarks || "-",
                    entryData: entry
                  });
                  break;

                case "Quality Control & Testing":
                  groupedData.qualityControl.push({
                    id: entry._id,
                    description: entry.qualityControlDetails?.testDescription || "",
                    location: entry.qualityControlDetails?.locationElement || "",
                    date: entry.qualityControlDetails?.testDate ?
                      format(new Date(entry.qualityControlDetails.testDate), "dd MMM yyyy") : "",
                    count: entry.qualityControlDetails?.numberOfTests || 0,
                    result: entry.qualityControlDetails?.results || "",
                    status: entry.qualityControlDetails?.status || "",
                    remark: entry.adminRemarks || "-",
                    entryData: entry
                  });
                  break;

                case "Safety Observation":
                  groupedData.safetyObservations.push({
                    id: entry._id,
                    item: entry.safetyObservationDetails?.itemName || "",
                    status: entry.safetyObservationDetails?.status || "",
                    remark: entry.adminRemarks || "-",
                    entryData: entry
                  });
                  break;

                case "Visitor Log":
                  groupedData.visitorLogs.push({
                    id: entry._id,
                    name: entry.visitorLogDetails?.visitorName || "",
                    company: entry.visitorLogDetails?.companyName || "",
                    designation: entry.visitorLogDetails?.designation || "",
                    purpose: entry.visitorLogDetails?.purposeOfVisit || "",
                    instructions: entry.visitorLogDetails?.instructionsDecisions || "",
                    timeIn: entry.visitorLogDetails?.timeIn || "",
                    timeOut: entry.visitorLogDetails?.timeOut || "",
                    remark: entry.adminRemarks || "-",
                    entryData: entry
                  });
                  break;

                case "Delay Hindrances":
                  groupedData.delaysHindrances.push({
                    id: entry._id,
                    description: entry.delayHindrancesDetails?.descriptionOfDelay || "",
                    duration: entry.delayHindrancesDetails?.duration || "",
                    reason: entry.delayHindrancesDetails?.reason || "",
                    remark: entry.adminRemarks || "-",
                    entryData: entry
                  });
                  break;

                case "Photograph":
                  groupedData.photographs.push({
                    id: entry._id,
                    src: entry.photographDetails?.photoPath
                      ? `${url}/${entry.photographDetails.photoPath.replace(/\\/g, '/')}`.replace(/([^:])\/\//g, '$1/')
                      : "https://via.placeholder.com/300x200?text=Photo+Placeholder",
                    activity: entry.photographDetails?.activityLocation || "",
                    ref: entry.photographDetails?.photoReferenceNo || "",
                    remark: entry.photographDetails?.remarks || "",
                    entryData: entry
                  });
                  break;
              }
            });
          }
        });

        console.log("Grouped Data:", groupedData);

        setDprData(groupedData);
      } else {
        console.log("No DPR data found, initializing with empty arrays");
        setDprData({
          workProgress: [],
          materialReceived: [],
          machinery: [],
          qualityControl: [],
          safetyObservations: [],
          visitorLogs: [],
          delaysHindrances: [],
          photographs: []
        });
      }
    } catch (err) {
      console.error("Error fetching DPR data:", err);
      showToast("error", "Error", "Failed to load DPR data");
    }
  };
  // Modular entry management like in AddDPR.js
  const addDPREntry = (section, entry) => {
    setDprData(prev => ({
      ...prev,
      [section]: [...prev[section], entry]
    }));
  };

  const updateDPREntry = (section, updatedEntry, oldId = null) => {
    setDprData(prev => {
      const sectionEntries = prev[section] || [];
      const idToMatch = oldId || updatedEntry.id;

      let found = false;
      let newSectionEntries = sectionEntries.map(entry => {
        if (entry.id === idToMatch) {
          found = true;
          return updatedEntry;
        }
        return entry;
      });

      if (!found) {
        newSectionEntries = [...newSectionEntries, updatedEntry];
      }

      return {
        ...prev,
        [section]: newSectionEntries
      };
    });
  };

  const deleteDPREntry = (section, entryId) => {
    setDprData(prev => ({
      ...prev,
      [section]: prev[section].filter(entry => entry.id !== entryId)
    }));
  };



  // Handle download DPR report
  const handleDownloadReport = async () => {
    try {
      setDownloading(true);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        responseType: 'blob'
      };

      const response = await axios.get(
        `${url}/api/dpr/download-report/${projectId}`,
        {
          ...config,
          params: {
            reportDate: reportDate
          }
        }
      );

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const urlBlob = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlBlob;

      // Generate filename
      const date = new Date(reportDate);
      const dateStr = date.toLocaleDateString('en-GB').replace(/\//g, '-');
      const projectName = project?.projectName?.replace(/[^a-zA-Z0-9]/g, '_') || 'DPR';
      link.download = `DPR_${projectName}_${dateStr}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlBlob);

      showToast("success", "Success", "DPR report downloaded successfully!");

    } catch (err) {
      console.error("Error downloading report:", err);
      showToast("error", "Error", "Failed to download report. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ p: { xs: 0.5, md: 1 } }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container maxWidth="xl" sx={{ p: { xs: 0.5, md: 1 } }}>
        <Alert severity="warning">Project not found</Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" disableGutters sx={{ p: 1.2, position: 'relative' }}>
        {/* LOADING OVERLAY FOR REFRESHING */}
        {refreshing && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backdropFilter: 'blur(3px)',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} thickness={4} sx={{ color: COLORS.SECONDARY }} />
              <Typography sx={{ ...TYPOGRAPHY.H4, mt: 2, color: COLORS.SECONDARY, fontWeight: 600 }}>
                Refreshing Data...
              </Typography>
            </Box>
          </Box>
        )}
        {/* Toast Notification */}
        <AppToast
          open={toast.open}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
        />

        <HeaderSection
          project={project}
          reportDate={reportDate}
          onDownload={handleDownloadReport}
          downloading={downloading}
          onClose={() => navigate(`/progress/project-overview/${projectId}`)}
        />

        <WorkProgressSection
          projectId={projectId}
          dprDate={reportDate}
          entries={dprData.workProgress}
          onAddEntry={(entry) => addDPREntry('workProgress', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('workProgress', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('workProgress', entryId)}
          refreshData={() => fetchDPRData(reportDate, { headers: { Authorization: token ? `Bearer ${token}` : undefined } })}
        />

        <MaterialReceivedSection
          projectId={projectId}
          dprDate={reportDate}
          entries={dprData.materialReceived}
          onAddEntry={(entry) => addDPREntry('materialReceived', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('materialReceived', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('materialReceived', entryId)}
          refreshData={() => fetchDPRData(reportDate, { headers: { Authorization: token ? `Bearer ${token}` : undefined } })}
        />

        <MachineryReportSection
          projectId={projectId}
          dprDate={reportDate}
          entries={dprData.machinery}
          onAddEntry={(entry) => addDPREntry('machinery', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('machinery', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('machinery', entryId)}
          refreshData={() => fetchDPRData(reportDate, { headers: { Authorization: token ? `Bearer ${token}` : undefined } })}
        />

        <QualityControlSection
          projectId={projectId}
          dprDate={reportDate}
          entries={dprData.qualityControl}
          onAddEntry={(entry) => addDPREntry('qualityControl', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('qualityControl', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('qualityControl', entryId)}
          refreshData={() => fetchDPRData(reportDate, { headers: { Authorization: token ? `Bearer ${token}` : undefined } })}
        />

        <SafetyObservationsSection
          projectId={projectId}
          dprDate={reportDate}
          entries={dprData.safetyObservations}
          onAddEntry={(entry) => addDPREntry('safetyObservations', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('safetyObservations', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('safetyObservations', entryId)}
          refreshData={() => fetchDPRData(reportDate, { headers: { Authorization: token ? `Bearer ${token}` : undefined } })}
        />

        <VisitorLogsSection
          projectId={projectId}
          dprDate={reportDate}
          entries={dprData.visitorLogs}
          onAddEntry={(entry) => addDPREntry('visitorLogs', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('visitorLogs', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('visitorLogs', entryId)}
          refreshData={() => fetchDPRData(reportDate, { headers: { Authorization: token ? `Bearer ${token}` : undefined } })}
        />

        <DelaysHindrancesSection
          projectId={projectId}
          dprDate={reportDate}
          entries={dprData.delaysHindrances}
          onAddEntry={(entry) => addDPREntry('delaysHindrances', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('delaysHindrances', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('delaysHindrances', entryId)}
          refreshData={() => fetchDPRData(reportDate, { headers: { Authorization: token ? `Bearer ${token}` : undefined } })}
        />

        <PhotographsSection
          projectId={projectId}
          dprDate={reportDate}
          entries={dprData.photographs}
          onAddEntry={(entry) => addDPREntry('photographs', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('photographs', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('photographs', entryId)}
          refreshData={() => fetchDPRData(reportDate, { headers: { Authorization: token ? `Bearer ${token}` : undefined } })}
          showToast={showToast}
        />

        <SubmitDPRButton
          projectId={projectId}
          dprDate={reportDate}
        />
      </Container>
    </LocalizationProvider>
  );
}

/* ========================= HEADER ========================= */

function HeaderSection({ project, reportDate, onClose, onDownload, downloading }) {
  // Format the report date for display

  const navigate = useNavigate();
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "DD/MM/YYYY";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };
  const handleClose = () => {
    navigate(-1); // <-- your edit report route
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Row 1 */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        {/* LEFT: TITLE + DOWNLOAD */}
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
            Edit Daily Progress Report (DPR)
          </Typography>

          <Button
            onClick={onDownload}
            disabled={downloading}
            startIcon={
              downloading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Icon name="download1" />
              )
            }
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              borderRadius: "7px",
              px: 2,
              height: 36,
              "&:hover": {
                backgroundColor: COLORS.PRIMARY,
              },
            }}
          >
            {downloading ? "Downloading..." : "Download"}
          </Button>
        </Box>

        {/* RIGHT: CLOSE */}
        <IconButton size="small" onClick={handleClose}>
          <Icon name="close3" color={COLORS.ERROR} />
        </IconButton>
      </Box>


      {/* Row 2 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
            Project Name:
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
            {project.projectName || "Hare Krishna Valley"}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
            Date:
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              borderRadius: 1,
              px: 1.2,
              py: 0.3,
              gap: 0.6,
              fontSize: TYPOGRAPHY.BODY_SMALL.fontSize,
              color: COLORS.MUTED_TEXT,
              minWidth: 110,
              justifyContent: "space-between",
            }}
          >
            {formatDisplayDate(reportDate)}
            <Icon name="calendar3" color={COLORS.LIGHT_SECONDARY} />
          </Box>
        </Box>
      </Box>

      {/* Row 3 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
            Address:
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
            {project.siteAddress || "Pune, Maharashtra"}
          </Typography>
        </Box>
        <Box />
      </Box>

      {/* Row 4 */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
            Developer:
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
            {project.developerName || "Vyankatesh Contracts"}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
            Engineer:
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
            {project.assignedEngineer?.name || project.assignedEngineerName || "Ashish Hase"}
          </Typography>
        </Box>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          mt: 1.5,
          height: "1px",
          backgroundColor: COLORS.LIGHT_SECONDARY,
          opacity: 0.7,
        }}
      />
    </Box>
  );
}

/* ========================= WORK PROGRESS ========================= */

const getRemarkCellStyles = () => ({
  ...TYPOGRAPHY.BODY_LARGE,
  color: COLORS.SECONDARY,
  wordWrap: 'break-word',
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  maxWidth: '100%',
  display: 'block',
  lineHeight: 1.4,
  flex: 1,
});

function WorkProgressSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({
      open: true,
      ...toastConfig
    });
  };

  const handleCloseToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  // Calculate totals
  const calculateTotals = () => {
    let totalQty = 0;
    let skilledTotals = {};
    let unskilledTotals = {};

    entries.forEach(entry => {
      totalQty += Number(entry.qty || 0);
      entry.skilled?.forEach(s => {
        const [type, qty] = s.split(' - ');
        const quantity = Number(qty || 0);
        if (type && !isNaN(quantity)) {
          skilledTotals[type] = (skilledTotals[type] || 0) + quantity;
        }
      });
      entry.unskilled?.forEach(u => {
        const [type, qty] = u.split(' - ');
        const quantity = Number(qty || 0);
        if (type && !isNaN(quantity)) {
          unskilledTotals[type] = (unskilledTotals[type] || 0) + quantity;
        }
      });
    });

    return {
      totalQty,
      totalSkilled: Object.entries(skilledTotals).map(([type, qty]) => `${type} - ${qty}`),
      totalUnskilled: Object.entries(unskilledTotals).map(([type, qty]) => `${type} - ${qty}`)
    };
  };

  const { totalQty, totalSkilled, totalUnskilled } = calculateTotals();

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleEditSuccess = (updatedEntry) => {
    onUpdateEntry(updatedEntry);
    setEditingId(null);
  };

  const handleAddEntry = (newEntry) => {
    onAddEntry(newEntry);
    setShowAddForm(false);
  };

  const handleDeleteEntry = async (entry) => {


    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Entry deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Work Progress & Manpower Deployment
      </Typography>

      {/* TABLE */}
      <Box>
        {/* HEADER ROW */}
        <FlexRow
          sx={{
            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            pb: 1,
          }}
        >
          <FlexCell w="40px" sx={{ pl: 1 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Sr.No.
            </Typography>
          </FlexCell>

          <FlexCell flex={2} sx={{ pl: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Work Description
            </Typography>
          </FlexCell>

          <FlexCell flex={1.5} sx={{ pl: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Contractor Name
            </Typography>
          </FlexCell>

          <FlexCell flex={1.5} sx={{ pl: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Skilled Labour
            </Typography>
          </FlexCell>

          <FlexCell flex={1.2} sx={{ pl: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Unskilled Labour
            </Typography>
          </FlexCell>

          <FlexCell w="60px" sx={{ pl: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Est. QTY
            </Typography>
          </FlexCell>

          <FlexCell w="80px" sx={{ pl: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Unit
            </Typography>
          </FlexCell>

          <FlexCell flex={1.2} sx={{ pl: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Remark
              </Typography>
            </Box>
          </FlexCell>
        </FlexRow>

        {/* DATA ROWS */}
        {entries.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
              No entries yet
            </Typography>
          </Box>
        ) : (
          entries.map((row, i) => (
            <React.Fragment key={row.id || i}>
              <FlexRow
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 1,
                }}
              >
                <FlexCell w="40px" sx={{ pl: 1 }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {i + 1}.
                  </Typography>
                </FlexCell>

                <FlexCell flex={2} sx={{ pl: 2 }}>
                  <Typography sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    color: COLORS.SECONDARY,
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                    lineHeight: 1.4
                  }}>
                    {row.work || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell flex={1.5} sx={{ pl: 2 }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {row.contractor || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell flex={1.5} sx={{ pl: 2 }}>
                  {row.skilled?.map((s, index) => (
                    <Typography
                      key={index}
                      sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}
                    >
                      {s}
                    </Typography>
                  ))}
                </FlexCell>

                <FlexCell flex={1.2} sx={{ pl: 2 }}>
                  {row.unskilled?.map((u, index) => (
                    <Typography
                      key={index}
                      sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}
                    >
                      {u}
                    </Typography>
                  ))}
                </FlexCell>

                <FlexCell w="60px" sx={{ pl: 2 }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {row.qty}
                  </Typography>
                </FlexCell>

                <FlexCell w="80px" sx={{ pl: 2 }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {row.unit}
                  </Typography>
                </FlexCell>

                <FlexCell flex={1.2} sx={{ pl: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={getRemarkCellStyles()}>
                      {row.remark}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditEntry(row)}
                        sx={{
                          padding: 0.5,
                          "&:hover": {
                            backgroundColor: `${COLORS.SUCCESS}15`,
                          }
                        }}
                      >
                        <Icon name="Action3" color="#8ED081" size={20} />
                      </IconButton>
                      {!row.id?.toString().startsWith('prefill-') && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteEntry(row)}
                          sx={{
                            padding: 0.5,
                            "&:hover": {
                              backgroundColor: `${COLORS.ERROR}15`,
                            }
                          }}
                        >
                          <Icon name="close2" color={COLORS.ERROR} size={20} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </FlexCell>
              </FlexRow>

              {/* EDIT FORM */}
              {editingId === row.id && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <AddWorkProgressForm
                    entry={row}
                    onCancel={handleCancelEdit}
                    projectId={projectId}
                    dprDate={dprDate}
                    onSuccess={handleEditSuccess}
                    refreshData={refreshData}
                    showToast={showToast}
                  />
                </Box>
              )}
            </React.Fragment>
          ))
        )}

        {/* TOTAL ROW */}
        {entries.length > 0 && (
          <FlexRow
            sx={{
              borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              py: 1,
              backgroundColor: '#f8f9fa'
            }}
          >
            <FlexCell w="40px" sx={{ pl: 1 }} />
            <FlexCell flex={2} sx={{ pl: 2 }} />
            <FlexCell flex={1.5} sx={{ pl: 2 }}>
              <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}>
                Total
              </Typography>
            </FlexCell>

            <FlexCell flex={1.5} sx={{ pl: 2 }}>
              {totalSkilled.map((s, index) => (
                <Typography
                  key={index}
                  sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}
                >
                  {s}
                </Typography>
              ))}
            </FlexCell>

            <FlexCell flex={1.2} sx={{ pl: 2 }}>
              {totalUnskilled.map((u, index) => (
                <Typography
                  key={index}
                  sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}
                >
                  {u}
                </Typography>
              ))}
            </FlexCell>

            <FlexCell w="60px" sx={{ pl: 2 }}>
              <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}>
                {totalQty}
              </Typography>
            </FlexCell>

            <FlexCell w="80px" sx={{ pl: 2 }} />
            <FlexCell flex={1.2} sx={{ pl: 2 }} />
          </FlexRow>
        )}
      </Box>

      {/* ADD BUTTON */}
      {!editingId && (
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <Button
            size="small"
            startIcon={<Icon name="plus2" />}
            onClick={() => setShowAddForm(true)}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              borderRadius: "7px",
              px: 2,
            }}
          >
            Add New Entry
          </Button>
        </Box>
      )}

      {/* ADD FORM */}
      {showAddForm && !editingId && (
        <Box mt={2}>
          <AddWorkProgressForm
            onCancel={() => setShowAddForm(false)}
            projectId={projectId}
            dprDate={dprDate}
            onAddEntry={handleAddEntry}
            refreshData={refreshData}
            showToast={showToast}
          />
        </Box>
      )}

      {/* AppToast Component */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

function AddWorkProgressForm({ onCancel, projectId, dprDate, onAddEntry, onUpdateEntry, refreshData, entry, onSuccess, showToast }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isEditMode = !!entry;

  const [formData, setFormData] = useState({
    workDescription: entry?.work || "",
    contractorName: entry?.contractor || "",
    skilledLabour: isEditMode && entry?.skilled ?
      Object.fromEntries(entry.skilled.map(s => {
        const [type, qty] = s.split(' - ');
        return [type, qty];
      })) : {
        Carpenter: "0",
        Fitter: "0",
        Mason: "0",
      },
    unskilledLabour: isEditMode && entry?.unskilled ?
      Object.fromEntries(entry.unskilled.map(u => {
        const [type, qty] = u.split(' - ');
        return [type, qty];
      })) : {
        "M/C": "0",
        "F/C": "0",
      },
    estimatedQuantity: entry?.qty || 0,
    unit: entry?.unit || "Person",
    customUnit: isEditMode && entry?.unit && !["Bags", "MT", "Cum", "Nos", "Sqm", "Kg", "Person"].includes(entry?.unit) ? entry?.unit : "",
    remark: entry?.remark || "",
  });

  const [newSkilled, setNewSkilled] = useState({ type: "", qty: "" });
  const [newUnskilled, setNewUnskilled] = useState({ type: "", qty: "" });

  const addNewSkilled = () => {
    if (newSkilled.type.trim() && newSkilled.qty.trim()) {
      setFormData({
        ...formData,
        skilledLabour: {
          ...formData.skilledLabour,
          [newSkilled.type]: newSkilled.qty
        }
      });
      setNewSkilled({ type: "", qty: "" });
    }
  };

  const addNewUnskilled = () => {
    if (newUnskilled.type.trim() && newUnskilled.qty.trim()) {
      setFormData({
        ...formData,
        unskilledLabour: {
          ...formData.unskilledLabour,
          [newUnskilled.type]: newUnskilled.qty
        }
      });
      setNewUnskilled({ type: "", qty: "" });
    }
  };

  const removeSkilled = (type) => {
    const newSkilled = { ...formData.skilledLabour };
    delete newSkilled[type];
    setFormData({ ...formData, skilledLabour: newSkilled });
  };

  const removeUnskilled = (type) => {
    const newUnskilled = { ...formData.unskilledLabour };
    delete newUnskilled[type];
    setFormData({ ...formData, unskilledLabour: newUnskilled });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      const filteredSkilledLabour = {};
      const filteredUnskilledLabour = {};

      Object.entries(formData.skilledLabour).forEach(([type, qty]) => {
        if (qty && qty !== "0" && Number(qty) > 0) {
          filteredSkilledLabour[type] = qty;
        }
      });

      Object.entries(formData.unskilledLabour).forEach(([type, qty]) => {
        if (qty && qty !== "0" && Number(qty) > 0) {
          filteredUnskilledLabour[type] = qty;
        }
      });

      const requestData = {
        projectId,
        reportType: "Work Progress & Manpower Deployment",
        title: `Work Progress - ${formData.workDescription || "Daily Report"}`,
        adminRemarks: formData.remark,
        reportDate: typeof dprDate === 'string' ? dprDate : format(dprDate, "yyyy-MM-dd"),
        workProgressDetails: {
          workDescription: formData.workDescription,
          contractorName: formData.contractorName,
          skilledLabour: filteredSkilledLabour,
          unskilledLabour: filteredUnskilledLabour,
          estimatedQuantity: Number(formData.estimatedQuantity) || 0,
          unit: formData.unit === 'Other' ? formData.customUnit : formData.unit,
          remarks: formData.remark,
        },
      };

      let response;

      if (isEditMode && entry?.id && !entry.id.toString().startsWith('prefill-')) {
        response = await axios.put(
          `${url}/api/dpr/update-entry/${entry.id}`,
          requestData,
          config
        );
      } else {
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      if (response && (response.status === 200 || response.status === 201)) {
        const responseData = response.data.dpr || response.data.data || response.data;
        setSuccess(true);

        const newEntry = {
          id: entry?.id || responseData._id || `temp-${Date.now()}`,
          _id: entry?._id || responseData._id,
          work: formData.workDescription,
          contractor: formData.contractorName,
          skilled: Object.entries(formData.skilledLabour).map(([type, qty]) => `${type} - ${qty}`),
          unskilled: Object.entries(formData.unskilledLabour).map(([type, qty]) => `${type} - ${qty}`),
          qty: formData.estimatedQuantity,
          unit: formData.unit === 'Other' ? formData.customUnit : formData.unit,
          remark: formData.remark || "-",
        };

        if (isEditMode && onSuccess) {
          onSuccess(newEntry);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(newEntry);
        }

        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Work progress entry updated successfully!" : "Work progress entry added successfully!"
          });
        }

        setTimeout(() => {
          if (refreshData) refreshData();
          if (onCancel) onCancel();
        }, 1500);
      }
    } catch (err) {
      console.error("Error saving Work Progress DPR:", err);
      const errorMsg = err.response?.data?.message || err.message || "Failed to save DPR entry";
      setError(errorMsg);
      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
      "&:hover fieldset": { borderColor: COLORS.SECONDARY },
      "&.Mui-focused fieldset": { borderColor: COLORS.SECONDARY },
    },
    "& input": { color: COLORS.PRIMARY, ...TYPOGRAPHY.BODY_LARGE, padding: "10px 12px" },
  };

  return (
    <Box sx={{ p: 2, border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: 2, bgcolor: "#fff" }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 2 }}>
        {isEditMode ? "Edit Work Progress Entry" : "Add New Work Progress Entry"}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}</Alert>}

      <Box display="flex" gap={3} mb={3}>
        <Box flex={1.6}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Work Description</Typography>
          <TextField fullWidth size="small" value={formData.workDescription} onChange={(e) => setFormData({ ...formData, workDescription: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Contractor Name</Typography>
          <TextField fullWidth size="small" value={formData.contractorName} onChange={(e) => setFormData({ ...formData, contractorName: e.target.value })} sx={inputSx} />
        </Box>
      </Box>

      <Box display="flex" gap={5} mb={3}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 1 }}>Skilled Labour</Typography>
          {Object.entries(formData.skilledLabour).map(([type, qty]) => (
            <Box key={type} display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <TextField size="small" value={type} sx={{ ...inputSx, width: 180 }} onChange={(e) => {
                const newSkilled = { ...formData.skilledLabour };
                delete newSkilled[type];
                newSkilled[e.target.value] = qty;
                setFormData({ ...formData, skilledLabour: newSkilled });
              }} />
              <TextField size="small" type="number" value={qty} sx={{ ...inputSx, width: 60 }} onChange={(e) => setFormData({ ...formData, skilledLabour: { ...formData.skilledLabour, [type]: e.target.value } })} />
              <IconButton onClick={() => removeSkilled(type)} sx={{ color: COLORS.ERROR }}><Icon name="close2" size={18} /></IconButton>
            </Box>
          ))}
          <Box display="flex" alignItems="center" gap={1.5}>
            <TextField size="small" placeholder="New Type" value={newSkilled.type} sx={{ ...inputSx, width: 180 }} onChange={(e) => setNewSkilled({ ...newSkilled, type: e.target.value })} />
            <TextField size="small" type="number" placeholder="Qty" value={newSkilled.qty} sx={{ ...inputSx, width: 60 }} onChange={(e) => setNewSkilled({ ...newSkilled, qty: e.target.value })} />
            <Button onClick={addNewSkilled} sx={{ bgcolor: COLORS.SECONDARY, color: "#fff", "&:hover": { bgcolor: COLORS.PRIMARY } }}>Add</Button>
          </Box>
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 1 }}>Unskilled Labour</Typography>
          {Object.entries(formData.unskilledLabour).map(([type, qty]) => (
            <Box key={type} display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <TextField size="small" value={type} sx={{ ...inputSx, width: 180 }} onChange={(e) => {
                const newUnskilled = { ...formData.unskilledLabour };
                delete newUnskilled[type];
                newUnskilled[e.target.value] = qty;
                setFormData({ ...formData, unskilledLabour: newUnskilled });
              }} />
              <TextField size="small" type="number" value={qty} sx={{ ...inputSx, width: 60 }} onChange={(e) => setFormData({ ...formData, unskilledLabour: { ...formData.unskilledLabour, [type]: e.target.value } })} />
              <IconButton onClick={() => removeUnskilled(type)} sx={{ color: COLORS.ERROR }}><Icon name="close2" size={18} /></IconButton>
            </Box>
          ))}
          <Box display="flex" alignItems="center" gap={1.5}>
            <TextField size="small" placeholder="New Type" value={newUnskilled.type} sx={{ ...inputSx, width: 180 }} onChange={(e) => setNewUnskilled({ ...newUnskilled, type: e.target.value })} />
            <TextField size="small" type="number" placeholder="Qty" value={newUnskilled.qty} sx={{ ...inputSx, width: 60 }} onChange={(e) => setNewUnskilled({ ...newUnskilled, qty: e.target.value })} />
            <Button onClick={addNewUnskilled} sx={{ bgcolor: COLORS.SECONDARY, color: "#fff", "&:hover": { bgcolor: COLORS.PRIMARY } }}>Add</Button>
          </Box>
        </Box>
      </Box>

      <Box display="flex" gap={3} mb={3}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Qty</Typography>
          <TextField fullWidth size="small" type="number" value={formData.estimatedQuantity} onChange={(e) => setFormData({ ...formData, estimatedQuantity: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Unit</Typography>
          <Select fullWidth size="small" value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value, customUnit: e.target.value === 'Other' ? formData.customUnit : '' })} sx={inputSx}>
            <MenuItem value="Bags">Bags</MenuItem>
            <MenuItem value="MT">MT</MenuItem>
            <MenuItem value="Cum">Cum</MenuItem>
            <MenuItem value="Nos">Nos</MenuItem>
            <MenuItem value="Sqm">Sqm</MenuItem>
            <MenuItem value="Kg">Kg</MenuItem>
            <MenuItem value="Person">Person</MenuItem>
            <MenuItem value="Ltr">Ltr</MenuItem>
            <MenuItem value="Rmt">Rmt</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
          {formData.unit === 'Other' && (
            <TextField
              fullWidth
              size="small"
              placeholder="Enter custom unit"
              value={formData.customUnit || ''}
              onChange={(e) => setFormData({ ...formData, customUnit: e.target.value })}
              sx={{ ...inputSx, mt: 1 }}
            />
          )}
        </Box>
        <Box flex={2}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Remark</Typography>
          <TextField fullWidth size="small" value={formData.remark} onChange={(e) => setFormData({ ...formData, remark: e.target.value })} sx={inputSx} />
        </Box>
      </Box>

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel} sx={{ color: COLORS.ERROR }}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading} sx={{ bgcolor: COLORS.SECONDARY, color: "#fff", "&:hover": { bgcolor: COLORS.PRIMARY } }}>
          {loading ? "Saving..." : isEditMode ? "Update Entry" : "Save Entry"}
        </Button>
      </Box>
    </Box>
  );
}



/* ========================= MATERIAL RECEIVED ========================= */

function MaterialReceivedSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData, fetchYesterdayClosingBalance }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  // Prefill yesterday's closing balances only for materials that don't have user data yet
  useEffect(() => {
    const fetchAllYesterdayBalances = async () => {
      if (!fetchYesterdayClosingBalance || !projectId || !dprDate) return;

      for (const material of MATERIAL_MASTER) {
        try {
          const existingEntry = entries.find(e => e.material === material.name);

          const isPrefillWithNoData = existingEntry &&
            existingEntry.id?.toString().startsWith('prefill-') &&
            (Number(existingEntry.received) === 0 || existingEntry.received === "-") &&
            (Number(existingEntry.consumed) === 0 || existingEntry.consumed === "-") &&
            (existingEntry.remark === "-" || !existingEntry.remark);

          if (existingEntry && !isPrefillWithNoData) {
            continue;
          }

          const yesterdayClosingBalance = await fetchYesterdayClosingBalance(material.name);

          if (yesterdayClosingBalance > 0) {
            const updatedEntry = {
              id: `prefill-${material.name}`,
              material: material.name,
              unit: material.unit,
              supplier: "-",
              challan: "-",
              opening: yesterdayClosingBalance,
              received: 0,
              total: yesterdayClosingBalance,
              consumed: 0,
              closing: yesterdayClosingBalance,
              remark: "-"
            };

            onUpdateEntry(updatedEntry);
          }
        } catch (error) {
          console.error(`Error fetching balance for ${material.name}:`, error);
        }
      }
    };

    fetchAllYesterdayBalances();
  }, [fetchYesterdayClosingBalance, projectId, dprDate, onUpdateEntry]);

  const allEntries = useMemo(() => {
    const combined = [...entries];

    MATERIAL_MASTER.forEach(master => {
      const alreadyHasEntry = entries.some(e => e.material === master.name);

      if (!alreadyHasEntry) {
        combined.push({
          id: `prefill-${master.name}`,
          material: master.name,
          unit: master.unit,
          supplier: "-",
          challan: "-",
          opening: 0,
          received: 0,
          total: 0,
          consumed: 0,
          closing: 0,
          remark: "-"
        });
      }
    });

    return combined;
  }, [entries]);

  const formatQty = (val) => String(val || 0).padStart(2, "0");

  const handleAddEntry = (entry) => {
    onAddEntry(entry);
    setShowAddForm(false);
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleEditSuccess = (updatedEntry, oldId) => {
    onUpdateEntry(updatedEntry, oldId);
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDeleteEntry = async (entry) => {


    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      if (!entry.id.toString().startsWith('prefill-')) {
        await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      }

      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Entry deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Material Received Report
      </Typography>

      <Box>
        <FlexRow
          sx={{
            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            pb: 1,
          }}
        >
          <FlexCell w="50px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Sr
            </Typography>
          </FlexCell>

          <FlexCell flex={2} pl={1}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Material Description
            </Typography>
          </FlexCell>

          <FlexCell flex={1.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Supplier Name
            </Typography>
          </FlexCell>

          <FlexCell flex={1.2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Challan No
            </Typography>
          </FlexCell>

          <FlexCell w="60px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Unit
            </Typography>
          </FlexCell>

          <FlexCell flex={2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Details
            </Typography>
          </FlexCell>

          <FlexCell flex={1.2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Remark
              </Typography>
            </Box>
          </FlexCell>
        </FlexRow>

        {allEntries.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
              No entries yet
            </Typography>
          </Box>
        ) : (
          allEntries.map((row, i) => (
            <React.Fragment key={row.id || i}>
              <FlexRow
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 1,
                }}
              >
                <FlexCell w="50px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {i + 1}.
                  </Typography>
                </FlexCell>

                <FlexCell flex={2} pl={1}>
                  <Typography sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    color: COLORS.SECONDARY,
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                    lineHeight: 1.4
                  }}>
                    {row.material || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell flex={1.5}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {row.supplier || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell flex={1.2}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {row.challan || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell w="60px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {row.unit || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell flex={2}>
                  {[
                    ["Opening Balance", row.opening],
                    ["Received Today", row.received],
                    ["Total Received", row.total],
                    ["Consumed Today", row.consumed],
                    ["Closing Balance", row.closing],
                  ].map(([label, value], index, arr) => (
                    <Box
                      key={label}
                      sx={{
                        borderBottom:
                          index !== arr.length - 1
                            ? `1px solid ${COLORS.LIGHT_SECONDARY}`
                            : "none",
                        pb: 0.4,
                        mb: 0.4,
                        pr: 2,
                        width: "80%",
                      }}
                    >
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          gap: 2.3,
                        }}
                      >
                        <span style={{ minWidth: "120px" }}>{label}</span>
                        <span>{formatQty(value)}</span>
                      </Typography>
                    </Box>
                  ))}
                </FlexCell>

                <FlexCell flex={1.2}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={getRemarkCellStyles()}>
                      {row.remark || "-"}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditEntry(row)}
                        sx={{
                          padding: 0.5,
                          "&:hover": {
                            backgroundColor: `${COLORS.SUCCESS}15`,
                          }
                        }}
                      >
                        <Icon name="Action3" color="#8ED081" size={20} />
                      </IconButton>
                      {!row.id.toString().startsWith('prefill-') && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteEntry(row)}
                          sx={{
                            padding: 0.5,
                            "&:hover": {
                              backgroundColor: `${COLORS.ERROR}15`,
                            }
                          }}
                        >
                          <Icon name="close2" color={COLORS.ERROR} size={20} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </FlexCell>
              </FlexRow>

              {editingId === row.id && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <AddMaterialForm
                    entry={row}
                    onCancel={handleCancelEdit}
                    projectId={projectId}
                    dprDate={dprDate}
                    onSuccess={handleEditSuccess}
                    refreshData={refreshData}
                    showToast={showToast}
                    fetchYesterdayClosingBalance={fetchYesterdayClosingBalance}
                  />
                </Box>
              )}
            </React.Fragment>
          ))
        )}

        {!editingId && (
          <Box display="flex" justifyContent="flex-end" mt={1.5}>
            <Button
              size="small"
              startIcon={<Icon name="plus2" />}
              onClick={() => setShowAddForm(true)}
              sx={{
                backgroundColor: COLORS.SECONDARY,
                color: "#fff",
                textTransform: "none",
                borderRadius: "7px",
                px: 2,
              }}
            >
              Add New Entry
            </Button>
          </Box>
        )}

        {showAddForm && !editingId && (
          <Box mt={2}>
            <AddMaterialForm
              onCancel={() => setShowAddForm(false)}
              projectId={projectId}
              dprDate={dprDate}
              onAddEntry={handleAddEntry}
              refreshData={refreshData}
              showToast={showToast}
              fetchYesterdayClosingBalance={fetchYesterdayClosingBalance}
            />
          </Box>
        )}
      </Box>

      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

function AddMaterialForm({ onCancel, projectId, dprDate, onAddEntry, onUpdateEntry, refreshData, entry, onSuccess, showToast, fetchYesterdayClosingBalance }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isEditMode = !!entry;
  const draftKey = `draft_dpr_material_${projectId}_${dprDate}_${entry?.id || 'new'}`;

  const [formData, setFormData, clearDraft] = useFormDraft(draftKey, {
    materialDescription: entry?.material || "",
    supplierName: entry?.supplier || "",
    challanNumber: entry?.challan || "",
    unit: entry?.unit && !["Bags", "MT", "Cum", "Nos", "Sqm", "Kg", "Ltr", "Rmt"].includes(entry?.unit) ? "Other" : (entry?.unit || "Bags"),
    customUnit: entry?.unit && !["Bags", "MT", "Cum", "Nos", "Sqm", "Kg", "Ltr", "Rmt"].includes(entry?.unit) ? entry?.unit : "",
    openingBalance: entry?.opening || 0,
    receivedToday: entry?.received || 0,
    consumedToday: entry?.consumed || 0,
    remark: entry?.remark || "",
  });

  const [calculatedValues, setCalculatedValues] = useState({
    totalReceived: entry?.total || 0,
    closingBalance: entry?.closing || 0,
  });

  useEffect(() => {
    const opening = Number(formData.openingBalance) || 0;
    const received = Number(formData.receivedToday) || 0;
    const consumed = Number(formData.consumedToday) || 0;

    const totalReceived = opening + received;
    const closingBalance = totalReceived - consumed;

    setCalculatedValues({
      totalReceived: totalReceived >= 0 ? totalReceived : 0,
      closingBalance: closingBalance >= 0 ? closingBalance : 0
    });
  }, [formData.openingBalance, formData.receivedToday, formData.consumedToday]);

  useEffect(() => {
    if (formData.materialDescription && fetchYesterdayClosingBalance && !isEditMode) {
      if (!formData.openingBalance || formData.openingBalance === 0) {
        const fetchBalance = async () => {
          const balance = await fetchYesterdayClosingBalance(formData.materialDescription);
          setFormData(prev => ({ ...prev, openingBalance: balance }));
        };
        fetchBalance();
      }
    }
  }, [formData.materialDescription, fetchYesterdayClosingBalance, isEditMode]);

  const handleSubmit = async () => {
    try {
      if (!formData.materialDescription.trim()) {
        setError("Material Description is required");
        return;
      }

      setLoading(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      const requestData = {
        projectId,
        reportType: "Material Received Report",
        title: `Material - ${formData.materialDescription}`,
        materialDescription: formData.materialDescription,
        supplierName: formData.supplierName || "",
        challanNumber: formData.challanNumber || "",
        unit: formData.unit === 'Other' ? formData.customUnit : formData.unit,
        adminRemarks: formData.remark || "",
        reportDate: typeof dprDate === 'string' ? dprDate : format(dprDate, "yyyy-MM-dd"),
        materialDetails: {
          openingBalance: Number(formData.openingBalance) || 0,
          receivedToday: Number(formData.receivedToday) || 0,
          totalReceived: calculatedValues.totalReceived,
          consumedToday: Number(formData.consumedToday) || 0,
          closingBalance: calculatedValues.closingBalance,
        }
      };

      let response;

      if (isEditMode && entry?.id && !entry.id.toString().startsWith('prefill-')) {
        response = await axios.put(
          `${url}/api/dpr/update-entry/${entry._id || entry.id}`,
          requestData,
          config
        );
      } else {
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      if (response && (response.status === 200 || response.status === 201)) {
        clearDraft();
        const responseData = response.data.dpr || response.data.data || response.data;
        setSuccess(true);

        const updatedEntry = {
          id: responseData._id,
          _id: responseData._id,
          material: formData.materialDescription,
          supplier: formData.supplierName,
          challan: formData.challanNumber,
          unit: formData.unit === 'Other' ? formData.customUnit : formData.unit,
          opening: Number(formData.openingBalance),
          received: Number(formData.receivedToday),
          total: calculatedValues.totalReceived,
          consumed: Number(formData.consumedToday),
          closing: calculatedValues.closingBalance,
          remark: formData.remark,
        };

        if (isEditMode && onSuccess) {
          onSuccess(updatedEntry, entry.id);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(updatedEntry);
        }

        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Material entry updated successfully!" : "Material entry added successfully!"
          });
        }

        setTimeout(() => {
          if (refreshData) refreshData();
          if (onCancel) onCancel();
        }, 1500);
      }
    } catch (err) {
      console.error("Error saving Material:", err);
      const errorMsg = err.response?.data?.message || err.message || "Failed to save material entry";
      setError(errorMsg);
      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
      "&:hover fieldset": { borderColor: COLORS.SECONDARY },
      "&.Mui-focused fieldset": { borderColor: COLORS.SECONDARY },
    },
    "& input": { color: COLORS.PRIMARY, ...TYPOGRAPHY.BODY_LARGE, padding: "10px 12px" },
  };

  return (
    <Box sx={{ p: 2, border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: 2, bgcolor: "#fff" }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
        {isEditMode ? "Edit Material Entry" : "Add New Material Entry"}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}</Alert>}

      <Box display="flex" gap={3} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Material Description *</Typography>
          <TextField fullWidth placeholder="Material Description" value={formData.materialDescription} onChange={(e) => setFormData({ ...formData, materialDescription: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Supplier Name</Typography>
          <TextField fullWidth placeholder="Supplier Name" value={formData.supplierName} onChange={(e) => setFormData({ ...formData, supplierName: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Challan Number</Typography>
          <TextField fullWidth placeholder="Challan No." value={formData.challanNumber} onChange={(e) => setFormData({ ...formData, challanNumber: e.target.value })} sx={inputSx} />
        </Box>
      </Box>

      <Box display="flex" gap={3} mb={2}>
        <Box width="200px">
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Unit</Typography>
          <Select fullWidth size="small" value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value, customUnit: e.target.value === 'Other' ? formData.customUnit : '' })} sx={inputSx}>
            <MenuItem value="Bags">Bags</MenuItem>
            <MenuItem value="MT">MT</MenuItem>
            <MenuItem value="Cum">Cum</MenuItem>
            <MenuItem value="Nos">Nos</MenuItem>
            <MenuItem value="Sqm">Sqm</MenuItem>
            <MenuItem value="Kg">Kg</MenuItem>
            <MenuItem value="Ltr">Ltr</MenuItem>
            <MenuItem value="Rmt">Rmt</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
        </Box>
        {formData.unit === 'Other' && (
          <Box width="200px">
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Custom Unit</Typography>
            <TextField fullWidth size="small" value={formData.customUnit} onChange={(e) => setFormData({ ...formData, customUnit: e.target.value })} sx={inputSx} />
          </Box>
        )}
      </Box>

      <Box display="flex" gap={2} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Opening Balance</Typography>
          <TextField fullWidth type="number" value={formData.openingBalance} onChange={(e) => setFormData({ ...formData, openingBalance: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Received Today</Typography>
          <TextField fullWidth type="number" value={formData.receivedToday} onChange={(e) => setFormData({ ...formData, receivedToday: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Total (Auto)</Typography>
          <TextField fullWidth value={calculatedValues.totalReceived} InputProps={{ readOnly: true }} sx={inputSx} />
        </Box>
      </Box>

      <Box display="flex" gap={2} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Consumed Today</Typography>
          <TextField fullWidth type="number" value={formData.consumedToday} onChange={(e) => setFormData({ ...formData, consumedToday: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Closing (Auto)</Typography>
          <TextField fullWidth value={calculatedValues.closingBalance} InputProps={{ readOnly: true }} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Remark</Typography>
          <TextField fullWidth value={formData.remark} onChange={(e) => setFormData({ ...formData, remark: e.target.value })} sx={inputSx} />
        </Box>
      </Box>

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel} sx={{ color: COLORS.ERROR }}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading} sx={{ bgcolor: COLORS.SECONDARY, color: "#fff", "&:hover": { bgcolor: COLORS.PRIMARY } }}>
          {loading ? "Saving..." : isEditMode ? "Update Entry" : "Save Entry"}
        </Button>
      </Box>
    </Box>
  );
}

/* ========================= MACHINERY REPORT ========================= */


function MachineryReportSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const allEntries = useMemo(() => {
    const combined = [...entries];
    EQUIPMENT_MASTER.forEach(equipment => {
      // Check if this equipment already exists in the real entries
      if (!entries.some(entry => entry.equipment === equipment)) {
        combined.push({
          id: `prefill-${equipment}`,
          equipment: equipment,
          supplier: "-",
          startReading: 0,
          endReading: 0,
          inTime: "-",
          outTime: "-",
          working: "-",
          planned: 0,
          actual: 0,
          remark: "-"
        });
      }
    });
    return combined;
  }, [entries]);

  const handleAddEntry = (entry) => {
    onAddEntry(entry);
    setShowAddForm(false);
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleEditSuccess = (updatedEntry, oldId) => {
    onUpdateEntry(updatedEntry, oldId);
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDeleteEntry = async (entry) => {


    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      if (!entry.id.toString().startsWith('prefill-')) {
        await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      }

      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Entry deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Machinery Report
      </Typography>

      <Box>
        <Box
          display="flex"
          alignItems="center"
          pb={1}
          sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}
        >
          <Box width="40px"><Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr.</Typography></Box>
          <Box flex={1.5}><Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Equipment Name</Typography></Box>
          <Box flex={1.5}><Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Supplier Name</Typography></Box>
          <Box flex={2}><Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Time</Typography></Box>
          <Box width="90px"><Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Planned Qty</Typography></Box>
          <Box width="90px"><Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Actual Qty</Typography></Box>
          <Box flex={1.4}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
            </Box>
          </Box>
        </Box>

        {allEntries.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
              No entries yet
            </Typography>
          </Box>
        ) : (
          allEntries.map((row, index) => (
            <React.Fragment key={row.id || index}>
              <Box
                display="flex"
                alignItems="flex-start"
                py={1.2}
                sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}
              >
                <Box width="40px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{index + 1}.</Typography>
                </Box>

                <Box flex={1.5}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.equipment || "-"}</Typography>
                </Box>

                <Box flex={1.5}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.supplier || "-"}</Typography>
                </Box>

                <Box flex={2}>
                  {/* Display Logic based on available data */}
                  {(Number(row.startReading) > 0 || Number(row.endReading) > 0) && ((row.inTime !== "" && row.inTime !== "-") || (row.outTime !== "" && row.outTime !== "-")) ? (
                    // Show all values when both readings and time are present
                    <>
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                        Start Reading: <span style={{ color: COLORS.PRIMARY }}>{row.startReading || "0"}</span>
                      </Typography>
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                        End Reading: <span style={{ color: COLORS.PRIMARY }}>{row.endReading || "0"}</span>
                      </Typography>
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                        In Time: <span style={{ color: COLORS.PRIMARY }}>{row.inTime}</span>
                      </Typography>
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                        Out Time: <span style={{ color: COLORS.PRIMARY }}>{row.outTime}</span>
                      </Typography>
                    </>
                  ) : (Number(row.startReading) > 0 || Number(row.endReading) > 0) ? (
                    // Show only readings when only readings are present
                    <>
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                        Start Reading: <span style={{ color: COLORS.PRIMARY }}>{row.startReading || "0"}</span>
                      </Typography>
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                        End Reading: <span style={{ color: COLORS.PRIMARY }}>{row.endReading || "0"}</span>
                      </Typography>
                    </>
                  ) : ((row.inTime !== "" && row.inTime !== "-") || (row.outTime !== "" && row.outTime !== "-")) ? (
                    // Show only in/out time when only time is present
                    <>
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                        In Time: <span style={{ color: COLORS.PRIMARY }}>{row.inTime}</span>
                      </Typography>
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                        Out Time: <span style={{ color: COLORS.PRIMARY }}>{row.outTime}</span>
                      </Typography>
                    </>
                  ) : (
                    // Show all fields with placeholders when nothing has values
                    <>
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                        Start Reading: <span style={{ color: COLORS.PRIMARY }}>{row.startReading || "0"}</span>
                      </Typography>
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                        End Reading: <span style={{ color: COLORS.PRIMARY }}>{row.endReading || "0"}</span>
                      </Typography>
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                        In Time: <span style={{ color: COLORS.PRIMARY }}>{(row.inTime === "" || row.inTime === "-") ? "-" : row.inTime}</span>
                      </Typography>
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                        Out Time: <span style={{ color: COLORS.PRIMARY }}>{(row.outTime === "" || row.outTime === "-") ? "-" : row.outTime}</span>
                      </Typography>
                    </>
                  )}
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    Working: <span style={{ color: COLORS.PRIMARY }}>{row.working || "-"}</span>
                  </Typography>
                </Box>

                <Box width="90px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{String(row.planned || 0).padStart(2, "0")}</Typography>
                </Box>

                <Box width="90px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{String(row.actual || 0).padStart(2, "0")}</Typography>
                </Box>

                <Box flex={1.4}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={getRemarkCellStyles()}>
                      {row.remark || "-"}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditEntry(row)}
                        sx={{
                          padding: 0.5,
                          "&:hover": {
                            backgroundColor: `${COLORS.SUCCESS}15`,
                          }
                        }}
                      >
                        <Icon name="Action3" color="#8ED081" size={20} />
                      </IconButton>
                      {!row.id.toString().startsWith('prefill-') && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteEntry(row)}
                          sx={{
                            padding: 0.5,
                            "&:hover": {
                              backgroundColor: `${COLORS.ERROR}15`,
                            }
                          }}
                        >
                          <Icon name="close2" color={COLORS.ERROR} size={20} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {editingId === row.id && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <AddMachineryForm
                    entry={row}
                    onCancel={handleCancelEdit}
                    projectId={projectId}
                    dprDate={dprDate}
                    onSuccess={handleEditSuccess}
                    refreshData={refreshData}
                    showToast={showToast}
                  />
                </Box>
              )}
            </React.Fragment>
          ))
        )}

        {!editingId && (
          <Box display="flex" justifyContent="flex-end" mt={1.5}>
            <Button
              size="small"
              startIcon={<Icon name="plus2" />}
              onClick={() => setShowAddForm(true)}
              sx={{
                backgroundColor: COLORS.SECONDARY,
                color: "#fff",
                textTransform: "none",
                borderRadius: "7px",
                px: 2,
              }}
            >
              Add New Entry
            </Button>
          </Box>
        )}

        {showAddForm && !editingId && (
          <Box mt={2}>
            <AddMachineryForm
              onCancel={() => setShowAddForm(false)}
              projectId={projectId}
              dprDate={dprDate}
              onAddEntry={handleAddEntry}
              refreshData={refreshData}
              showToast={showToast}
            />
          </Box>
        )}
      </Box>

      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

function AddMachineryForm({ onCancel, projectId, dprDate, onAddEntry, refreshData, entry, onSuccess, showToast }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [timeIn, setTimeIn] = useState(null);
  const [timeOut, setTimeOut] = useState(null);
  const [workingHours, setWorkingHours] = useState(entry?.working || "");

  const isEditMode = !!entry;

  useEffect(() => {
    if (isEditMode) {
      if (entry.inTime && entry.inTime !== "-") {
        const [time, modifier] = entry.inTime.split(' ');
        let [hours, minutes] = time.split(':');
        if (modifier === 'PM' && hours !== '12') hours = parseInt(hours) + 12;
        if (modifier === 'AM' && hours === '12') hours = '00';
        const date = new Date();
        date.setHours(parseInt(hours), parseInt(minutes), 0);
        setTimeIn(date);
      }
      if (entry.outTime && entry.outTime !== "-") {
        const [time, modifier] = entry.outTime.split(' ');
        let [hours, minutes] = time.split(':');
        if (modifier === 'PM' && hours !== '12') hours = parseInt(hours) + 12;
        if (modifier === 'AM' && hours === '12') hours = '00';
        const date = new Date();
        date.setHours(parseInt(hours), parseInt(minutes), 0);
        setTimeOut(date);
      }
    }
  }, [isEditMode, entry]);

  const [formData, setFormData] = useState({
    equipmentName: entry?.equipment || "",
    supplierName: entry?.supplier || "",
    startReading: entry?.startReading || 0,
    endReading: entry?.endReading || 0,
    plannedQty: entry?.planned || 0,
    actualQty: entry?.actual || 0,
    remark: entry?.remark || "",
  });

  useEffect(() => {
    const updateWorkingHours = async () => {
      if (Number(formData.endReading) > 0 || Number(formData.startReading) > 0) {
        try {
          const config = { headers: { Authorization: token ? `Bearer ${token}` : undefined } };
          const response = await axios.post(`${url}/api/timeDuration/calculate-reading`, {
            startReading: formData.startReading,
            endReading: formData.endReading
          }, config);
          if (response.data.success) setWorkingHours(response.data.workingHours);
        } catch (err) { console.error(err); }
        return;
      }
      if (timeIn && timeOut) {
        const diff = timeOut.getTime() - timeIn.getTime();
        const hrs = Math.floor(diff / 3600000);
        const mins = Math.floor((diff % 3600000) / 60000);
        setWorkingHours(`${hrs} hrs ${mins} mins`);
      }
    };
    updateWorkingHours();
  }, [formData.startReading, formData.endReading, timeIn, timeOut, token]);

  const handleSubmit = async () => {
    try {
      if (!formData.equipmentName.trim()) {
        setError("Equipment Name is required");
        return;
      }

      setLoading(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      const requestData = {
        projectId,
        reportType: "Machinery Report",
        title: `Machinery - ${formData.equipmentName}`,
        adminRemarks: formData.remark || "",
        reportDate: typeof dprDate === 'string' ? dprDate : format(dprDate, "yyyy-MM-dd"),
        machineryDetails: {
          equipmentName: formData.equipmentName,
          supplierName: formData.supplierName || "",
          startReading: Number(formData.startReading) || 0,
          endReading: Number(formData.endReading) || 0,
          inTime: timeIn ? format(timeIn, "hh:mm a") : "",
          outTime: timeOut ? format(timeOut, "hh:mm a") : "",
          workingHours: workingHours,
          plannedQty: Number(formData.plannedQty) || 0,
          actualQty: Number(formData.actualQty) || 0,
          remarks: formData.remark || "",
        },
      };

      let response;

      if (isEditMode && entry?.id && !entry.id.toString().startsWith('prefill-')) {
        response = await axios.put(
          `${url}/api/dpr/update-entry/${entry._id || entry.id}`,
          requestData,
          config
        );
      } else {
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      if (response && (response.status === 200 || response.status === 201)) {
        const responseData = response.data.dpr || response.data.data || response.data;
        setSuccess(true);

        const updatedEntry = {
          id: responseData._id,
          _id: responseData._id,
          equipment: formData.equipmentName,
          supplier: formData.supplierName,
          startReading: Number(formData.startReading),
          endReading: Number(formData.endReading),
          inTime: timeIn ? format(timeIn, "hh:mm a") : "",
          outTime: timeOut ? format(timeOut, "hh:mm a") : "",
          working: workingHours,
          planned: Number(formData.plannedQty),
          actual: Number(formData.actualQty),
          remark: formData.remark,
        };

        if (isEditMode && onSuccess) {
          onSuccess(updatedEntry, entry.id);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(updatedEntry);
        }

        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Machinery entry updated successfully!" : "Machinery entry added successfully!"
          });
        }

        setTimeout(() => {
          if (refreshData) refreshData();
          if (onCancel) onCancel();
        }, 1500);
      }
    } catch (err) {
      console.error("Error saving Machinery:", err);
      const errorMsg = err.response?.data?.message || err.message || "Failed to save machinery entry";
      setError(errorMsg);
      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
      "&:hover fieldset": { borderColor: COLORS.SECONDARY },
      "&.Mui-focused fieldset": { borderColor: COLORS.SECONDARY },
    },
    "& input": { color: COLORS.PRIMARY, ...TYPOGRAPHY.BODY_LARGE, padding: "10px 12px" },
  };

  return (
    <Box sx={{ p: 2, border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: 2, bgcolor: "#fff" }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
        {isEditMode ? "Edit Machinery Entry" : "Add New Machinery Entry"}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}</Alert>}

      <Box display="flex" gap={3} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Equipment Name *</Typography>
          <TextField fullWidth placeholder="Equipment Name" value={formData.equipmentName} onChange={(e) => setFormData({ ...formData, equipmentName: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Supplier Name</Typography>
          <TextField fullWidth placeholder="Supplier Name" value={formData.supplierName} onChange={(e) => setFormData({ ...formData, supplierName: e.target.value })} sx={inputSx} />
        </Box>
      </Box>

      <Box display="flex" gap={3} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Start Reading</Typography>
          <TextField fullWidth type="number" value={formData.startReading} onChange={(e) => setFormData({ ...formData, startReading: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>End Reading</Typography>
          <TextField fullWidth type="number" value={formData.endReading} onChange={(e) => setFormData({ ...formData, endReading: e.target.value })} sx={inputSx} />
        </Box>
      </Box>

      <Box display="flex" gap={3} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>In Time</Typography>
          <TimePicker value={timeIn} onChange={(val) => setTimeIn(val)} renderInput={(params) => <TextField {...params} fullWidth sx={inputSx} />} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Out Time</Typography>
          <TimePicker value={timeOut} onChange={(val) => setTimeOut(val)} renderInput={(params) => <TextField {...params} fullWidth sx={inputSx} />} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Working Hours (Auto)</Typography>
          <TextField fullWidth value={workingHours} InputProps={{ readOnly: true }} sx={inputSx} />
        </Box>
      </Box>

      <Box display="flex" gap={3} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Planned Qty</Typography>
          <TextField fullWidth type="number" value={formData.plannedQty} onChange={(e) => setFormData({ ...formData, plannedQty: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Actual Qty</Typography>
          <TextField fullWidth type="number" value={formData.actualQty} onChange={(e) => setFormData({ ...formData, actualQty: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Remark</Typography>
          <TextField fullWidth value={formData.remark} onChange={(e) => setFormData({ ...formData, remark: e.target.value })} sx={inputSx} />
        </Box>
      </Box>

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel} sx={{ color: COLORS.ERROR }}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading} sx={{ bgcolor: COLORS.SECONDARY, color: "#fff", "&:hover": { bgcolor: COLORS.PRIMARY } }}>
          {loading ? "Saving..." : isEditMode ? "Update Entry" : "Save Entry"}
        </Button>
      </Box>
    </Box>
  );
}
/* ========================= QUALITY CONTROL ========================= */

function QualityControlSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleAddEntry = (entry) => {
    onAddEntry(entry);
    setShowAddForm(false);
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleEditSuccess = (updatedEntry, oldId) => {
    onUpdateEntry(updatedEntry, oldId);
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDeleteEntry = async (entry) => {


    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Entry deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Quality Control & Testing
      </Typography>

      <Box>
        <FlexRow>
          <FlexCell w="40px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr</Typography>
          </FlexCell>
          <FlexCell flex={2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Test Description</Typography>
          </FlexCell>
          <FlexCell flex={2.2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Location / Element</Typography>
          </FlexCell>
          <FlexCell w="140px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Date</Typography>
          </FlexCell>
          <FlexCell w="80px" sx={{ pl: 3 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>No of Test</Typography>
          </FlexCell>
          <FlexCell flex={1.5} sx={{ pl: 1 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Results</Typography>
          </FlexCell>
          <FlexCell w="100px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Status</Typography>
          </FlexCell>
          <FlexCell flex={1.5}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
            </Box>
          </FlexCell>
        </FlexRow>

        {entries.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
              No entries yet
            </Typography>
          </Box>
        ) : (
          entries.map((row, i) => (
            <React.Fragment key={row.id || i}>
              <FlexRow>
                <FlexCell w="40px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{i + 1}</Typography>
                </FlexCell>
                <FlexCell flex={2}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, wordWrap: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>
                    {row.description || "-"}
                  </Typography>
                </FlexCell>
                <FlexCell flex={2.2}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, wordWrap: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>
                    {row.location || "-"}
                  </Typography>
                </FlexCell>
                <FlexCell w="140px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.date || "-"}</Typography>
                </FlexCell>
                <FlexCell w="80px" sx={{ pl: 3 }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.count}</Typography>
                </FlexCell>
                <FlexCell flex={1.5} sx={{ pl: 1 }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, wordWrap: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>
                    {row.result || "-"}
                  </Typography>
                </FlexCell>
                <FlexCell w="100px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.status || "-"}</Typography>
                </FlexCell>
                <FlexCell flex={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={getRemarkCellStyles()}>
                      {row.remark || "-"}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditEntry(row)}
                        sx={{ padding: 0.5, "&:hover": { backgroundColor: `${COLORS.SUCCESS}15` } }}
                      >
                        <Icon name="Action3" color="#8ED081" size={20} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteEntry(row)}
                        sx={{ padding: 0.5, "&:hover": { backgroundColor: `${COLORS.ERROR}15` } }}
                      >
                        <Icon name="close2" color={COLORS.ERROR} size={20} />
                      </IconButton>
                    </Box>
                  </Box>
                </FlexCell>
              </FlexRow>

              {editingId === row.id && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <AddQualityForm
                    entry={row}
                    onCancel={handleCancelEdit}
                    projectId={projectId}
                    dprDate={dprDate}
                    onSuccess={handleEditSuccess}
                    refreshData={refreshData}
                    showToast={showToast}
                  />
                </Box>
              )}
            </React.Fragment>
          ))
        )}
      </Box>

      {!editingId && (
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <Button
            size="small"
            startIcon={<Icon name="plus2" />}
            onClick={() => setShowAddForm(true)}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              borderRadius: "7px",
              px: 2,
            }}
          >
            Add New Entry
          </Button>
        </Box>
      )}

      {showAddForm && !editingId && (
        <Box mt={2}>
          <AddQualityForm
            onCancel={() => setShowAddForm(false)}
            projectId={projectId}
            dprDate={dprDate}
            onAddEntry={handleAddEntry}
            refreshData={refreshData}
            showToast={showToast}
          />
        </Box>
      )}

      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

function AddQualityForm({ onCancel, projectId, dprDate, onAddEntry, refreshData, entry, onSuccess, showToast }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isEditMode = !!entry;

  const [formData, setFormData] = useState({
    testDescription: entry?.description || "",
    locationElement: entry?.location || "",
    testDate: entry?.date ?
      new Date(entry.date.split(' ').reverse().join('-')).toISOString().split('T')[0] :
      (typeof dprDate === 'string' ? dprDate : format(dprDate, "yyyy-MM-dd")),
    numberOfTests: entry?.count || 0,
    results: entry?.result || "",
    status: entry?.status || "",
    remarks: entry?.remark || "",
  });

  const handleSubmit = async () => {
    try {
      if (!formData.testDescription.trim()) {
        setError("Test Description is required");
        return;
      }

      setLoading(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      const requestData = {
        projectId,
        reportType: "Quality Control & Testing",
        title: `Quality Test - ${formData.testDescription}`,
        adminRemarks: formData.remarks || "",
        reportDate: typeof dprDate === 'string' ? dprDate : format(dprDate, "yyyy-MM-dd"),
        qualityControlDetails: {
          testDescription: formData.testDescription || "",
          locationElement: formData.locationElement || "",
          testDate: formData.testDate ? new Date(formData.testDate).toISOString() : new Date().toISOString(),
          numberOfTests: Number(formData.numberOfTests) || 0,
          results: formData.results || "",
          status: formData.status || "PASS",
          remarks: formData.remarks || "",
        }
      };

      let response;

      if (isEditMode && entry?.id && !entry.id.toString().startsWith('prefill-')) {
        response = await axios.put(
          `${url}/api/dpr/update-entry/${entry._id || entry.id}`,
          requestData,
          config
        );
      } else {
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      if (response && (response.status === 200 || response.status === 201)) {
        const responseData = response.data.dpr || response.data.data || response.data;
        setSuccess(true);

        const newEntry = {
          id: responseData._id,
          _id: responseData._id,
          description: formData.testDescription,
          location: formData.locationElement,
          date: formData.testDate,
          count: Number(formData.numberOfTests),
          result: formData.results,
          status: formData.status,
          remark: formData.remarks,
        };

        if (isEditMode && onSuccess) {
          onSuccess(newEntry, entry.id);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(newEntry);
        }

        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Quality entry updated successfully!" : "Quality entry added successfully!"
          });
        }

        setTimeout(() => {
          if (refreshData) refreshData();
          if (onCancel) onCancel();
        }, 1500);
      }
    } catch (err) {
      console.error("Error saving Quality Control:", err);
      const errorMsg = err.response?.data?.message || err.message || "Failed to save quality entry";
      setError(errorMsg);
      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
      "&:hover fieldset": { borderColor: COLORS.SECONDARY },
      "&.Mui-focused fieldset": { borderColor: COLORS.SECONDARY },
    },
    "& input": { color: COLORS.PRIMARY, ...TYPOGRAPHY.BODY_LARGE, padding: "10px 12px" },
  };

  return (
    <Box sx={{ p: 2, border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: 2, bgcolor: "#fff" }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
        {isEditMode ? "Edit Quality Entry" : "Add New Quality Entry"}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}</Alert>}

      <Box display="flex" gap={3} mb={2}>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Test Description *</Typography>
          <TextField fullWidth placeholder="Test Description" value={formData.testDescription} onChange={(e) => setFormData({ ...formData, testDescription: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Location / Element</Typography>
          <TextField fullWidth placeholder="Location / Element" value={formData.locationElement} onChange={(e) => setFormData({ ...formData, locationElement: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Date</Typography>
          <TextField fullWidth type="date" value={formData.testDate} onChange={(e) => setFormData({ ...formData, testDate: e.target.value })} sx={inputSx} />
        </Box>
      </Box>

      <Box display="flex" gap={3} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>No of Tests</Typography>
          <TextField fullWidth type="number" placeholder="No of Tests" value={formData.numberOfTests} onChange={(e) => setFormData({ ...formData, numberOfTests: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Results</Typography>
          <TextField fullWidth placeholder="Results" value={formData.results} onChange={(e) => setFormData({ ...formData, results: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Status Pass / Fail</Typography>
          <TextField fullWidth value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} placeholder="Enter status" sx={inputSx} />
        </Box>
      </Box>

      <Box mb={3}>
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Remark</Typography>
        <TextField fullWidth multiline rows={2} value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} sx={{ ...inputSx, "& .MuiOutlinedInput-root": { height: "auto" } }} />
      </Box>

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel} sx={{ color: COLORS.ERROR }}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading} sx={{ bgcolor: COLORS.SECONDARY, color: "#fff", "&:hover": { bgcolor: COLORS.PRIMARY } }}>
          {loading ? "Saving..." : isEditMode ? "Update Entry" : "Save Entry"}
        </Button>
      </Box>
    </Box>
  );
}

/* ========================= SAFETY OBSERVATIONS ========================= */

function SafetyObservationsSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });
  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleAddEntry = (entry) => {
    onAddEntry(entry);
    setShowAddForm(false);
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleEditSuccess = (updatedEntry, oldId) => {
    onUpdateEntry(updatedEntry, oldId);
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDeleteEntry = async (entry) => {


    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Entry deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  return (
    <Box mt={4} sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Safety Observations
      </Typography>

      <Box>
        <FlexRow>
          <FlexCell w="40px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr</Typography>
          </FlexCell>
          <FlexCell flex={2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Item</Typography>
          </FlexCell>
          <FlexCell w="120px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Status</Typography>
          </FlexCell>
          <FlexCell flex={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
            </Box>
          </FlexCell>
        </FlexRow>

        {entries.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
              No entries yet
            </Typography>
          </Box>
        ) : (
          entries.map((row, i) => (
            <React.Fragment key={row.id || i}>
              <FlexRow>
                <FlexCell w="40px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{i + 1}</Typography>
                </FlexCell>
                <FlexCell flex={2}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, wordWrap: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>
                    {row.item || row.itemName || "-"}
                  </Typography>
                </FlexCell>
                <FlexCell w="120px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.status || "-"}</Typography>
                </FlexCell>
                <FlexCell flex={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={getRemarkCellStyles()}>
                      {row.remark || row.remarks || "-"}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditEntry(row)}
                        sx={{ padding: 0.5, "&:hover": { backgroundColor: `${COLORS.SUCCESS}15` } }}
                      >
                        <Icon name="Action3" color="#8ED081" size={20} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteEntry(row)}
                        sx={{ padding: 0.5, "&:hover": { backgroundColor: `${COLORS.ERROR}15` } }}
                      >
                        <Icon name="close2" color={COLORS.ERROR} size={20} />
                      </IconButton>
                    </Box>
                  </Box>
                </FlexCell>
              </FlexRow>

              {editingId === row.id && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <AddSafetyObservationForm
                    entry={row}
                    onCancel={handleCancelEdit}
                    projectId={projectId}
                    dprDate={dprDate}
                    onSuccess={handleEditSuccess}
                    refreshData={refreshData}
                    showToast={showToast}
                  />
                </Box>
              )}
            </React.Fragment>
          ))
        )}
      </Box>

      {!editingId && (
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <Button
            size="small"
            startIcon={<Icon name="plus2" />}
            onClick={() => setShowAddForm(true)}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              borderRadius: "7px",
              px: 2,
            }}
          >
            Add New Entry
          </Button>
        </Box>
      )}

      {showAddForm && !editingId && (
        <Box mt={2}>
          <AddSafetyObservationForm
            onCancel={() => setShowAddForm(false)}
            projectId={projectId}
            dprDate={dprDate}
            onAddEntry={handleAddEntry}
            refreshData={refreshData}
            showToast={showToast}
          />
        </Box>
      )}

      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

function AddSafetyObservationForm({ onCancel, projectId, dprDate, onAddEntry, refreshData, entry, onSuccess, showToast }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isEditMode = !!entry;

  const [formData, setFormData] = useState({
    itemName: entry?.item || entry?.itemName || "",
    status: entry?.status || "",
    remarks: entry?.remark || entry?.remarks || "",
  });

  const handleSubmit = async () => {
    try {
      if (!formData.itemName.trim()) {
        setError("Item Name is required");
        return;
      }

      setLoading(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      const requestData = {
        projectId,
        reportType: "Safety Observation",
        title: `Safety - ${formData.itemName}`,
        adminRemarks: formData.remarks || "",
        reportDate: typeof dprDate === 'string' ? dprDate : format(dprDate, "yyyy-MM-dd"),
        safetyObservationDetails: {
          itemName: formData.itemName || "",
          status: formData.status || "YES",
          remarks: formData.remarks || "",
        }
      };

      let response;

      if (isEditMode && entry?.id && !entry.id.toString().startsWith('prefill-')) {
        response = await axios.put(
          `${url}/api/dpr/update-entry/${entry._id || entry.id}`,
          requestData,
          config
        );
      } else {
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      if (response && (response.status === 200 || response.status === 201)) {
        const responseData = response.data.dpr || response.data.data || response.data;
        setSuccess(true);

        const newEntry = {
          id: responseData._id,
          _id: responseData._id,
          item: formData.itemName,
          itemName: formData.itemName,
          status: formData.status,
          remark: formData.remarks,
          remarks: formData.remarks,
        };

        if (isEditMode && onSuccess) {
          onSuccess(newEntry, entry.id);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(newEntry);
        }

        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Safety observation updated successfully!" : "Safety observation added successfully!"
          });
        }

        setTimeout(() => {
          if (refreshData) refreshData();
          if (onCancel) onCancel();
        }, 1500);
      }
    } catch (err) {
      console.error("Error saving Safety Observation:", err);
      const errorMsg = err.response?.data?.message || err.message || "Failed to save safety observation";
      setError(errorMsg);
      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
      "&:hover fieldset": { borderColor: COLORS.SECONDARY },
      "&.Mui-focused fieldset": { borderColor: COLORS.SECONDARY },
    },
    "& input": { color: COLORS.PRIMARY, ...TYPOGRAPHY.BODY_LARGE, padding: "10px 12px" },
  };

  return (
    <Box sx={{ p: 2, border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: 2, bgcolor: "#fff" }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
        {isEditMode ? "Edit Safety Observation" : "Add New Safety Observation"}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}</Alert>}

      <Box display="flex" gap={3} mb={2}>
        <Box flex={2}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Item Name *</Typography>
          <TextField fullWidth placeholder="Item Name" value={formData.itemName} onChange={(e) => setFormData({ ...formData, itemName: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Status</Typography>
          <TextField fullWidth placeholder="Status" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} sx={inputSx} />
        </Box>
      </Box>

      <Box mb={3}>
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Remark</Typography>
        <TextField fullWidth multiline rows={2} value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} sx={{ ...inputSx, "& .MuiOutlinedInput-root": { height: "auto" } }} />
      </Box>

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel} sx={{ color: COLORS.ERROR }}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading} sx={{ bgcolor: COLORS.SECONDARY, color: "#fff", "&:hover": { bgcolor: COLORS.PRIMARY } }}>
          {loading ? "Saving..." : isEditMode ? "Update Entry" : "Save Entry"}
        </Button>
      </Box>
    </Box>
  );
}

/* ========================= VISITOR LOGS ========================= */

function VisitorLogsSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleAddEntry = (entry) => {
    onAddEntry(entry);
    setShowAddForm(false);
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleEditSuccess = (updatedEntry, oldId) => {
    onUpdateEntry(updatedEntry, oldId);
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDeleteEntry = async (entry) => {


    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Visitor log deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  return (
    <Box mt={4} sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Visitor Logs
      </Typography>

      <Box>
        <FlexRow sx={{ pb: 1 }}>
          <FlexCell w="40px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr.</Typography>
          </FlexCell>
          <FlexCell flex={1.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Visitor Details</Typography>
          </FlexCell>
          <FlexCell flex={1.2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Purpose of Visit</Typography>
          </FlexCell>
          <FlexCell flex={1.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Instruction / Decisions</Typography>
          </FlexCell>
          <FlexCell w="100px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Time In</Typography>
          </FlexCell>
          <FlexCell w="100px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Time Out</Typography>
          </FlexCell>
          <FlexCell flex={1.5}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
            </Box>
          </FlexCell>
        </FlexRow>

        {entries.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
              No entries yet
            </Typography>
          </Box>
        ) : (
          entries.map((row, i) => (
            <React.Fragment key={row.id || i}>
              <FlexRow sx={{ py: 1 }}>
                <FlexCell w="40px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{i + 1}.</Typography>
                </FlexCell>
                <FlexCell flex={1.5}>
                  <Box display="flex" flexDirection="column">
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      {row.name || row.visitorName || "-"}
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT_SECONDARY, lineHeight: 1.3 }}>
                      {row.company || row.companyName || "-"}
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT_SECONDARY, lineHeight: 1.3 }}>
                      {row.designation || "-"}
                    </Typography>
                  </Box>
                </FlexCell>
                <FlexCell flex={1.2}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, wordWrap: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>
                    {row.purpose || row.purposeOfVisit || "-"}
                  </Typography>
                </FlexCell>
                <FlexCell flex={1.5}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, wordWrap: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>
                    {row.instructions || row.instructionsDecisions || "-"}
                  </Typography>
                </FlexCell>
                <FlexCell w="100px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.timeIn || "-"}</Typography>
                </FlexCell>
                <FlexCell w="100px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.timeOut || "-"}</Typography>
                </FlexCell>
                <FlexCell flex={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={getRemarkCellStyles()}>
                      {row.remark || row.remarks || "-"}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditEntry(row)}
                        sx={{ padding: 0.5, "&:hover": { backgroundColor: `${COLORS.SUCCESS}15` } }}
                      >
                        <Icon name="Action3" color="#8ED081" size={20} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteEntry(row)}
                        sx={{ padding: 0.5, "&:hover": { backgroundColor: `${COLORS.ERROR}15` } }}
                      >
                        <Icon name="close2" color={COLORS.ERROR} size={20} />
                      </IconButton>
                    </Box>
                  </Box>
                </FlexCell>
              </FlexRow>

              {editingId === row.id && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <AddVisitorLogForm
                    entry={row}
                    onCancel={handleCancelEdit}
                    projectId={projectId}
                    dprDate={dprDate}
                    onSuccess={handleEditSuccess}
                    refreshData={refreshData}
                    showToast={showToast}
                  />
                </Box>
              )}
            </React.Fragment>
          ))
        )}
      </Box>

      {!editingId && (
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <Button
            size="small"
            startIcon={<Icon name="plus2" />}
            onClick={() => setShowAddForm(true)}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              borderRadius: "7px",
              px: 2,
            }}
          >
            Add New Entry
          </Button>
        </Box>
      )}

      {showAddForm && !editingId && (
        <Box mt={2}>
          <AddVisitorLogForm
            onCancel={() => setShowAddForm(false)}
            projectId={projectId}
            dprDate={dprDate}
            onAddEntry={handleAddEntry}
            refreshData={refreshData}
            showToast={showToast}
          />
        </Box>
      )}

      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

function AddVisitorLogForm({ onCancel, projectId, dprDate, onAddEntry, refreshData, entry, onSuccess, showToast }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [visitorTimeIn, setVisitorTimeIn] = useState(null);
  const [visitorTimeOut, setVisitorTimeOut] = useState(null);

  const isEditMode = !!entry;

  // Parse time strings to Date objects for edit mode
  useEffect(() => {
    if (isEditMode && entry?.timeIn && entry.timeIn !== "-") {
      const timeString = entry.timeIn;
      const [time, modifier] = timeString.split(' ');
      let [hours, minutes] = time.split(':');

      if (modifier === 'PM' && hours !== '12') {
        hours = parseInt(hours, 10) + 12;
      }
      if (modifier === 'AM' && hours === '12') {
        hours = '00';
      }

      const date = new Date();
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0);
      setVisitorTimeIn(date);
    }

    if (isEditMode && entry?.timeOut && entry.timeOut !== "-") {
      const timeString = entry.timeOut;
      const [time, modifier] = timeString.split(' ');
      let [hours, minutes] = time.split(':');

      if (modifier === 'PM' && hours !== '12') {
        hours = parseInt(hours, 10) + 12;
      }
      if (modifier === 'AM' && hours === '12') {
        hours = '00';
      }

      const date = new Date();
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0);
      setVisitorTimeOut(date);
    }
  }, [isEditMode, entry]);

  const [formData, setFormData] = useState({
    visitorName: entry?.name || entry?.visitorName || "",
    companyName: entry?.company || entry?.companyName || "",
    designation: entry?.designation || "",
    purposeOfVisit: entry?.purpose || entry?.purposeOfVisit || "",
    instructionsDecisions: entry?.instructions || entry?.instructionsDecisions || "",
    remarks: entry?.remark || entry?.remarks || ""
  });

  const handleSubmit = async () => {
    try {
      if (!formData.visitorName.trim()) {
        setError("Visitor Name is required");
        return;
      }

      setLoading(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      // Format times from TimePicker or use default
      const formatTimeFromPicker = (time) => {
        if (!time) return "09:00 AM";
        return format(time, "hh:mm a");
      };

      const timeIn = visitorTimeIn ? formatTimeFromPicker(visitorTimeIn) :
        (entry?.timeIn && entry.timeIn !== "-" ? entry.timeIn : "09:00 AM");

      const timeOut = visitorTimeOut ? formatTimeFromPicker(visitorTimeOut) :
        (entry?.timeOut && entry.timeOut !== "-" ? entry.timeOut : "05:00 PM");

      const requestData = {
        projectId,
        reportType: "Visitor Log",
        title: `Visitor - ${formData.visitorName}`,
        adminRemarks: formData.remarks || "",
        reportDate: typeof dprDate === 'string' ? dprDate : format(dprDate, "yyyy-MM-dd"),
        visitorLogDetails: {
          visitorName: formData.visitorName || "",
          companyName: formData.companyName || "",
          designation: formData.designation || "",
          purposeOfVisit: formData.purposeOfVisit || "",
          instructionsDecisions: formData.instructionsDecisions || "",
          timeIn: timeIn,
          timeOut: timeOut,
          remarks: formData.remarks || ""
        }
      };

      let response;

      if (isEditMode && entry?.id && !entry.id.toString().startsWith('prefill-')) {
        response = await axios.put(
          `${url}/api/dpr/update-entry/${entry._id || entry.id}`,
          requestData,
          config
        );
      } else {
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      if (response && (response.status === 200 || response.status === 201)) {
        const responseData = response.data.dpr || response.data.data || response.data;
        setSuccess(true);

        const newEntry = {
          id: responseData._id,
          _id: responseData._id,
          name: formData.visitorName,
          visitorName: formData.visitorName,
          company: formData.companyName,
          companyName: formData.companyName,
          designation: formData.designation,
          purpose: formData.purposeOfVisit,
          purposeOfVisit: formData.purposeOfVisit,
          instructions: formData.instructionsDecisions,
          instructionsDecisions: formData.instructionsDecisions,
          timeIn: timeIn,
          timeOut: timeOut,
          remark: formData.remarks,
          remarks: formData.remarks,
        };

        if (isEditMode && onSuccess) {
          onSuccess(newEntry, entry.id);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(newEntry);
        }

        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Visitor log updated successfully!" : "Visitor log added successfully!"
          });
        }

        setTimeout(() => {
          if (refreshData) refreshData();
          if (onCancel) onCancel();
        }, 1500);
      }
    } catch (err) {
      console.error("Error saving Visitor Log:", err);
      const errorMsg = err.response?.data?.message || err.message || "Failed to save visitor log";
      setError(errorMsg);
      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
      "&:hover fieldset": { borderColor: COLORS.SECONDARY },
      "&.Mui-focused fieldset": { borderColor: COLORS.SECONDARY },
    },
    "& input": { color: COLORS.PRIMARY, ...TYPOGRAPHY.BODY_LARGE, padding: "10px 12px" },
  };

  return (
    <Box sx={{ p: 2, border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: 2, bgcolor: "#fff" }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
        {isEditMode ? "Edit Visitor Log" : "Add New Visitor Log"}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}</Alert>}

      <Box display="flex" gap={3} mb={2}>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Visitor Name *</Typography>
          <TextField fullWidth placeholder="Visitor Name" value={formData.visitorName} onChange={(e) => setFormData({ ...formData, visitorName: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Company Name</Typography>
          <TextField fullWidth placeholder="Company Name" value={formData.companyName} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Designation</Typography>
          <TextField fullWidth placeholder="Designation" value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} sx={inputSx} />
        </Box>
      </Box>

      <Box display="flex" gap={3} mb={2}>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Purpose of Visit</Typography>
          <TextField fullWidth placeholder="Purpose of Visit" value={formData.purposeOfVisit} onChange={(e) => setFormData({ ...formData, purposeOfVisit: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Instruction / Decisions</Typography>
          <TextField fullWidth placeholder="Instruction / Decisions" value={formData.instructionsDecisions} onChange={(e) => setFormData({ ...formData, instructionsDecisions: e.target.value })} sx={inputSx} />
        </Box>
      </Box>

      <Box display="flex" gap={3} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Time In</Typography>
          <TimePicker
            value={visitorTimeIn}
            onChange={(newValue) => setVisitorTimeIn(newValue)}
            renderInput={(params) => <TextField {...params} fullWidth sx={inputSx} />}
          />
        </Box>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Time Out</Typography>
          <TimePicker
            value={visitorTimeOut}
            onChange={(newValue) => setVisitorTimeOut(newValue)}
            renderInput={(params) => <TextField {...params} fullWidth sx={inputSx} />}
          />
        </Box>
      </Box>

      <Box mb={3}>
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Remark</Typography>
        <TextField fullWidth multiline rows={2} value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} sx={{ ...inputSx, "& .MuiOutlinedInput-root": { height: "auto" } }} />
      </Box>

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel} sx={{ color: COLORS.ERROR }}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading} sx={{ bgcolor: COLORS.SECONDARY, color: "#fff", "&:hover": { bgcolor: COLORS.PRIMARY } }}>
          {loading ? "Saving..." : isEditMode ? "Update Entry" : "Save Entry"}
        </Button>
      </Box>
    </Box>
  );
}


/* ========================= DELAYS & HINDRANCES ========================= */

function DelaysHindrancesSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleAddEntry = (entry) => {
    onAddEntry(entry);
    setShowAddForm(false);
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleEditSuccess = (updatedEntry, oldId) => {
    onUpdateEntry(updatedEntry, oldId);
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDeleteEntry = async (entry) => {


    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Delay/hindrance deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  return (
    <Box mt={4} sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Delays & Hindrances
      </Typography>

      <Box>
        <FlexRow sx={{ pb: 1 }}>
          <FlexCell w="40px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr</Typography>
          </FlexCell>
          <FlexCell flex={2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Description of Delay</Typography>
          </FlexCell>
          <FlexCell w="120px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Duration</Typography>
          </FlexCell>
          <FlexCell flex={1.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Reason</Typography>
          </FlexCell>
          <FlexCell flex={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
            </Box>
          </FlexCell>
        </FlexRow>

        {entries.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
              No entries yet
            </Typography>
          </Box>
        ) : (
          entries.map((row, i) => (
            <React.Fragment key={row.id || i}>
              <FlexRow sx={{ py: 1 }}>
                <FlexCell w="40px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{i + 1}</Typography>
                </FlexCell>
                <FlexCell flex={2}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, wordWrap: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>
                    {row.description || "-"}
                  </Typography>
                </FlexCell>
                <FlexCell w="120px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.duration || "-"}</Typography>
                </FlexCell>
                <FlexCell flex={1.5}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, wordWrap: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>
                    {row.reason || "-"}
                  </Typography>
                </FlexCell>
                <FlexCell flex={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={getRemarkCellStyles()}>
                      {row.remark || "-"}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditEntry(row)}
                        sx={{ padding: 0.5, "&:hover": { backgroundColor: `${COLORS.SUCCESS}15` } }}
                      >
                        <Icon name="Action3" color="#8ED081" size={20} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteEntry(row)}
                        sx={{ padding: 0.5, "&:hover": { backgroundColor: `${COLORS.ERROR}15` } }}
                      >
                        <Icon name="close2" color={COLORS.ERROR} size={20} />
                      </IconButton>
                    </Box>
                  </Box>
                </FlexCell>
              </FlexRow>

              {editingId === row.id && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <AddDelayForm
                    entry={row}
                    onCancel={handleCancelEdit}
                    projectId={projectId}
                    dprDate={dprDate}
                    onSuccess={handleEditSuccess}
                    refreshData={refreshData}
                    showToast={showToast}
                  />
                </Box>
              )}
            </React.Fragment>
          ))
        )}
      </Box>

      {!editingId && (
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <Button
            size="small"
            startIcon={<Icon name="plus2" />}
            onClick={() => setShowAddForm(true)}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              borderRadius: "7px",
              px: 2,
            }}
          >
            Add New Entry
          </Button>
        </Box>
      )}

      {showAddForm && !editingId && (
        <Box mt={2}>
          <AddDelayForm
            onCancel={() => setShowAddForm(false)}
            projectId={projectId}
            dprDate={dprDate}
            onAddEntry={handleAddEntry}
            refreshData={refreshData}
            showToast={showToast}
          />
        </Box>
      )}

      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

function AddDelayForm({ onCancel, projectId, dprDate, onAddEntry, refreshData, entry, onSuccess, showToast }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isEditMode = !!entry;

  const [formData, setFormData] = useState({
    descriptionOfDelay: entry?.description || "",
    duration: entry?.duration || "",
    reason: entry?.reason || "",
    remarks: entry?.remark || ""
  });

  const handleSubmit = async () => {
    try {
      if (!formData.descriptionOfDelay.trim()) {
        setError("Description is required");
        return;
      }

      setLoading(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      const requestData = {
        projectId,
        reportType: "Delay Hindrances",
        title: `Delay - ${formData.descriptionOfDelay.substring(0, 20)}`,
        adminRemarks: formData.remarks || "",
        reportDate: typeof dprDate === 'string' ? dprDate : format(dprDate, "yyyy-MM-dd"),
        delayHindrancesDetails: {
          descriptionOfDelay: formData.descriptionOfDelay || "",
          duration: formData.duration || "",
          reason: formData.reason || "",
          remarks: formData.remarks || ""
        }
      };

      let response;

      if (isEditMode && entry?.id && !entry.id.toString().startsWith('prefill-')) {
        response = await axios.put(
          `${url}/api/dpr/update-entry/${entry._id || entry.id}`,
          requestData,
          config
        );
      } else {
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      if (response && (response.status === 200 || response.status === 201)) {
        const responseData = response.data.dpr || response.data.data || response.data;
        setSuccess(true);

        const newEntry = {
          id: responseData._id,
          _id: responseData._id,
          description: formData.descriptionOfDelay,
          duration: formData.duration,
          reason: formData.reason,
          remark: formData.remarks,
        };

        if (isEditMode && onSuccess) {
          onSuccess(newEntry, entry.id);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(newEntry);
        }

        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Delay updated successfully!" : "Delay added successfully!"
          });
        }

        setTimeout(() => {
          if (refreshData) refreshData();
          if (onCancel) onCancel();
        }, 1500);
      }
    } catch (err) {
      console.error("Error saving Delay Hindrance:", err);
      const errorMsg = err.response?.data?.message || err.message || "Failed to save delay hindrance";
      setError(errorMsg);
      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
      "&:hover fieldset": { borderColor: COLORS.SECONDARY },
      "&.Mui-focused fieldset": { borderColor: COLORS.SECONDARY },
    },
    "& input": { color: COLORS.PRIMARY, ...TYPOGRAPHY.BODY_LARGE, padding: "10px 12px" },
  };

  return (
    <Box sx={{ p: 2, border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: 2, bgcolor: "#fff" }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
        {isEditMode ? "Edit Delay Report" : "Add New Delay Report"}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}</Alert>}

      <Box display="flex" gap={3} mb={2}>
        <Box flex={2}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Description of Delay *</Typography>
          <TextField fullWidth placeholder="Description of Delay" value={formData.descriptionOfDelay} onChange={(e) => setFormData({ ...formData, descriptionOfDelay: e.target.value })} sx={inputSx} />
        </Box>
        <Box width="120px">
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Duration</Typography>
          <TextField fullWidth placeholder="Duration" value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} sx={inputSx} />
        </Box>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Reason</Typography>
          <TextField fullWidth placeholder="Reason" value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} sx={inputSx} />
        </Box>
      </Box>

      <Box mb={3} width="50%">
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>Remark</Typography>
        <TextField fullWidth multiline rows={2} value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} sx={{ ...inputSx, "& .MuiOutlinedInput-root": { height: "auto" } }} />
      </Box>

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel} sx={{ color: COLORS.ERROR }}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading} sx={{ bgcolor: COLORS.SECONDARY, color: "#fff", "&:hover": { bgcolor: COLORS.PRIMARY } }}>
          {loading ? "Saving..." : isEditMode ? "Update Entry" : "Save Entry"}
        </Button>
      </Box>
    </Box>
  );
}

/* ========================= PHOTOGRAPHS ========================= */

function PhotographsSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const formatLocalDate = (date) => {
    const localDate = new Date(date);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T${String(localDate.getHours()).padStart(2, '0')}:${String(localDate.getMinutes()).padStart(2, '0')}:${String(localDate.getSeconds()).padStart(2, '0')}.${String(localDate.getMilliseconds()).padStart(3, '0')}Z`;
  };

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDeleteEntry = async (entry) => {


    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Photograph deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  const handleSaveNewPhoto = async (photoData) => {
    try {
      setShowAddForm(false);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'multipart/form-data'
        }
      };

      const formData = new FormData();

      if (photoData.file) {
        formData.append('photos', photoData.file);
      } else if (photoData.imageUrl && photoData.imageUrl.startsWith('data:image')) {
        const base64Response = await fetch(photoData.imageUrl);
        const blob = await base64Response.blob();
        const file = new File([blob], `photo_${Date.now()}.png`, { type: 'image/png' });
        formData.append('photos', file);
      }

      formData.append('projectId', projectId);
      formData.append('reportType', 'Photograph');
      formData.append('title', `Photograph - ${photoData.activityLocation || 'Daily Report'}`);
      formData.append('adminRemarks', photoData.remarks || '');
      formData.append('reportDate', formatLocalDate(dprDate));

      const photographDetails = {
        activityLocation: photoData.activityLocation || "",
        remarks: photoData.remarks || "",
        shapes: photoData.shapes || []
      };

      formData.append('photographDetails', JSON.stringify(photographDetails));

      const response = await axios.post(`${url}/api/dpr/add`, formData, config);

      if (response.status === 200 || response.status === 201) {
        const responseData = response.data.data || response.data.dpr || response.data;

        const newEntry = {
          id: responseData._id,
          _id: responseData._id,
          activity: responseData.photographDetails?.activityLocation || photoData.activityLocation,
          remark: responseData.adminRemarks || responseData.photographDetails?.remarks || photoData.remarks,
          src: responseData.photographDetails?.photoPath
            ? `${url}/${responseData.photographDetails.photoPath.replace(/\\/g, '/')}`.replace(/([^:])\/\//g, '$1/')
            : photoData.imageUrl,
          photographDetails: {
            ...responseData.photographDetails,
            shapes: photoData.shapes || []
          }
        };

        onAddEntry(newEntry);

        showToast({
          type: "success",
          title: "Success",
          message: "Photograph added successfully!"
        });
      }
    } catch (err) {
      console.error("Error saving photo:", err);
      showToast({
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to save photograph"
      });
    }
  };

  const handleUpdatePhoto = async (entryId, photoData) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'multipart/form-data'
        }
      };

      const formData = new FormData();

      if (photoData.file) {
        formData.append('photos', photoData.file);
      }

      formData.append('projectId', projectId);
      formData.append('reportType', 'Photograph');
      formData.append('title', `Photograph - ${photoData.activityLocation || 'Updated Photo'}`);
      formData.append('adminRemarks', photoData.remarks || '');
      formData.append('reportDate', formatLocalDate(dprDate));

      const photographDetails = {
        activityLocation: photoData.activityLocation || "",
        remarks: photoData.remarks || "",
        shapes: photoData.shapes || []
      };

      formData.append('photographDetails', JSON.stringify(photographDetails));

      const response = await axios.put(`${url}/api/dpr/update-entry/${entryId}`, formData, config);

      if (response.status === 200 || response.status === 201) {
        const responseData = response.data.data || response.data.dpr || response.data;
        setEditingId(null);

        const updatedEntry = {
          id: responseData._id,
          _id: responseData._id,
          activity: responseData.photographDetails?.activityLocation || photoData.activityLocation,
          remark: responseData.adminRemarks || responseData.photographDetails?.remarks || photoData.remarks,
          src: responseData.photographDetails?.photoPath
            ? `${url}/${responseData.photographDetails.photoPath.replace(/\\/g, '/')}`.replace(/([^:])\/\//g, '$1/') + `?v=${Date.now()}`
            : (photoData.imageUrl || entries.find(e => e.id === entryId)?.src),
          photographDetails: {
            ...responseData.photographDetails,
            shapes: photoData.shapes || []
          }
        };

        onUpdateEntry(updatedEntry, entryId);

        showToast({
          type: "success",
          title: "Success",
          message: "Photograph updated successfully!"
        });
      }
    } catch (err) {
      console.error("Error updating photo:", err);
      showToast({
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to update photograph"
      });
    }
  };

  return (
    <Box mt={4} sx={{ mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT }}>
          Photographs
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 3 }}>
        {entries.length === 0 ? (
          <Box sx={{ gridColumn: "1 / -1", py: 3, textAlign: 'center' }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
              No photographs yet
            </Typography>
          </Box>
        ) : (
          entries.map((p, index) => (
            <React.Fragment key={p.id || index}>
              <Box>
                <Box display="flex" justifyContent="flex-end" mb={0.5}>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteEntry(p)}
                    sx={{ padding: 0.5, "&:hover": { backgroundColor: "rgba(220, 53, 69, 0.1)" } }}
                  >
                    <Icon name="close2" size={16} color={COLORS.ERROR} />
                  </IconButton>
                </Box>

                <Box
                  component="img"
                  src={p.src}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/300x200?text=Image+Not+Found";
                  }}
                  sx={{ width: "100%", height: 220, objectFit: "contain", display: "block", mb: 1.5 }}
                  alt={`DPR Photo - ${p.activity || 'No description'}`}
                />

                <Box>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.PRIMARY, fontWeight: 600, mb: 0.3 }}>Activity / Location:</Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.PRIMARY, wordWrap: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>{p.activity || "-"}</Typography>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.PRIMARY, fontWeight: 600, mb: 0.3 }}>Remark:</Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.PRIMARY, wordWrap: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>{p.remark || p.photographDetails?.remarks || "-"}</Typography>
                  </Box>

                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      size="small"
                      startIcon={<Icon name="Action3" color="#8ED081" size={16} />}
                      onClick={() => handleEditEntry(p)}
                      sx={{ color: "#8ED081", textTransform: "none", fontSize: "0.75rem", padding: "2px 8px", minWidth: "auto", "&:hover": { backgroundColor: "rgba(142, 208, 129, 0.1)" } }}
                    >
                      Edit
                    </Button>
                  </Box>
                </Box>

                {editingId === p.id && (
                  <PhotoUploadWithDrawingDPR
                    open={true}
                    maxPhotos={1}
                    initialData={{
                      activityLocation: p.activity || "",
                      remarks: p.remark || p.photographDetails?.remarks || "",
                      imageUrl: p.src,
                      shapes: p.photographDetails?.shapes || []
                    }}
                    onCancel={handleCancelEdit}
                    onSave={(photoData) => handleUpdatePhoto(p.id, photoData)}
                  />
                )}
              </Box>
            </React.Fragment>
          ))
        )}
      </Box>

      <Box display="flex" justifyContent="flex-end" mt={1.5}>
        <Button
          size="small"
          startIcon={<Icon name="plus2" />}
          onClick={() => setShowAddForm(true)}
          sx={{ backgroundColor: COLORS.SECONDARY, color: "#fff", textTransform: "none", borderRadius: "7px", px: 2 }}
        >
          Add New Entry
        </Button>
      </Box>

      {showAddForm && (
        <PhotoUploadWithDrawingDPR
          open={true}
          maxPhotos={1}
          onCancel={() => setShowAddForm(false)}
          onSave={handleSaveNewPhoto}
        />
      )}

      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

/* ========================= SUBMIT BUTTON ========================= */

function SubmitDPRButton({ projectId, dprDate }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleSubmitAllDPR = async () => {
    try {
      setLoading(true);
      setError(null);

      // We don't need to submit anything since entries are already saved individually
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSuccess(true);
      setLoading(false);

      showToast({
        type: "success",
        title: "Success",
        message: "DPR updated successfully!"
      });
      setTimeout(() => navigate(-1), 1500);

    } catch (err) {
      console.error("Error submitting DPR:", err);
      const errorMsg = err.response?.data?.message || "Failed to update DPR";
      setError(errorMsg);

      showToast({
        type: "error",
        title: "Error",
        message: errorMsg
      });
      setLoading(false);
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="center" mt={5} mb={5}>
        <Button
          onClick={handleSubmitAllDPR}
          disabled={loading}
          sx={{
            backgroundColor: COLORS.SECONDARY,
            color: "#fff",
            textTransform: "uppercase",
            px: 5,
            py: 1.5,
            borderRadius: "8px",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: COLORS.PRIMARY,
            }
          }}
        >
          {loading ? "Updating..." : "Update Daily Progress Report"}
        </Button>
      </Box>

      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </>
  );
}

/* ========================= FLEX HELPERS ========================= */

function FlexRow({ children, bold, sx }) {
  return (
    <Box
      display="flex"
      alignItems="flex-start" // Standardized to top-alignment
      py={1}
      borderBottom={`1px solid ${COLORS.LIGHT_SECONDARY}`}
      sx={{ fontWeight: bold ? 600 : 400, ...sx }}
    >
      {children}
    </Box>
  );
}

function FlexCell({ children, flex, w, color, sx }) {
  return (
    <Box
      sx={{
        flex: flex || "none",
        width: w,
        fontSize: TYPOGRAPHY.BODY_STANDARD.fontSize,
        color: color ? COLORS.SECONDARY : COLORS.TEXT,
        paddingLeft: sx?.pl || 0,
        wordWrap: "break-word",
        whiteSpace: "normal",
        overflowWrap: "break-word",
        lineHeight: 1.4,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}